import React, { useState, useRef, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../components/LanguageSelector";
import {
    Upload,
    Clock,
    MapPin,
    Wrench,
    Camera,
    AlertCircle,
    Calendar,
    Building,
    Home,
    Phone,
    Mail,
    X,
    Video,
    Info,
    DollarSign,
    Settings,
    Zap,
    Search,
} from "lucide-react";
import OrientationLock from "../../components/ui/OrientationLock";

// Google Maps API TypeScript declarations
declare global {
    interface Window {
        google: any;
    }
}
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import PhoneInput from "../../components/ui/PhoneInput";
import CurrencyDropdown from "../../components/ui/CurrencyDropdown";
import SharedFooter3 from "../../Footer/SharedFooter3";
import SharedHeader from "../../Headers/SharedHeader";
import { createUserJobs, getTypeCategories, useAppDispatch } from "../../redux";
import { getAllCurrencies } from "../../redux/Actions/AuthAction";
import { uploadContractFile } from "../../redux/Slices/contractSlice";
import { getAllServiceAreas, createServiceArea } from "../../redux/Actions/ServiceAreaAction";
import { fetchCategoryFormSettings } from "../../redux/Actions/CategoryFormSettingsAction";
import { getAllCountries, createCountry } from "../../redux/Actions/AdminAction";
import { GlobalService } from "../../modules";
import { useSelector } from "react-redux";
import { selectLoaderValue } from "../../redux/Slices/loadingSlice";
import Spinner from "../../modules/shared/components/Spinner";
import { TypeEnum } from "../../modules";
import AIDescriptionGenerator from "../../components/ui/AIDescriptionGenerator";
import {
    extractLocationFromPlace,
    findOrCreateCountry,
    getProvincesByCountry,
    generateMockPostalCode,
    LocationData
} from "../../lib/locationUtils";
import {
    detectUserCurrency,
    getCachedCurrencyPreference,
    setCachedCurrencyPreference,
    findCurrencyIdByCode as utilFindCurrencyIdByCode,
    getCurrencySymbol,
    getDynamicCurrencySymbol
} from "../../lib/currencyUtils";

interface PlumberRequestFormProps {
    formData?: any;
    onSubmit?: (data: any) => void;
    onBack?: () => void;
    isEditMode?: boolean;
    initialData?: any;
}

const PlumberRequestForm: React.FC<PlumberRequestFormProps> = ({
    formData: parentFormData,
    onSubmit,
    onBack,
    isEditMode = false,
    initialData = null
}) => {
    const { t } = useTranslation();
    // Form validation state
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);

    // Function to get initial form data
    const getInitialFormData = () => {
        if (isEditMode && initialData) {
            return {
                description: initialData.description || "",
                requestType: initialData.requestType || "",
                serviceType: initialData.serviceType ? { id: Number(initialData.serviceType.id) } : { id: 1 },
                projectTimeline: initialData.projectTimeline || "",
                deadlineType: initialData.deadlineType ? { id: Number(initialData.deadlineType.id) } : { id: 1 },
                projectScope: initialData.projectScope || {
                    description: "",
                    timeline: "",
                    budget: {
                        type: "fixed",
                        amount: "",
                        currency: "USD",
                    },
                },
                budgetType: initialData.budgetType ? { id: Number(initialData.budgetType.id) } : { id: 1 },
                budget: initialData.budget || null,
                currency: initialData.currency || { id: "USD" },
                title: initialData.title || "",
                contactName: initialData.contactName || "",
                contactPhone: initialData.contactPhone || "",
                contactEmail: initialData.contactEmail || "",
                canContactByWhatsapp: initialData.canContactByWhatsapp || false,
                contactWhatsapp: initialData.contactWhatsapp || "",
                address: {
                    street: initialData.street || "",
                    address: initialData.address || "",
                    city: initialData.city || "",
                    province: initialData.province || "",
                    municipality: initialData.municipality || "",
                    postalcode: initialData.postalCode?.toString() || "",
                    floor: initialData.floor || null,
                    isElevatorAvailable: initialData.isElevatorAvailable || false,
                    isStairsRequired: initialData.isStairsRequired || false,
                    propertyType: initialData.propertyType ? { id: Number(initialData.propertyType.id) } : { id: 1 },
                },
                emergency: {
                    isEmergency: initialData.isEmergency || (initialData.deadlineType && Number(initialData.deadlineType.id) === 5) || false,
                    calculatedCost: initialData.calculatedCost || 0,
                    baseRate: initialData.baseRate || 0,
                    timeMultiplier: initialData.timeMultiplier || 1,
                    dayMultiplier: initialData.dayMultiplier || 1,
                    specialDateMultiplier: initialData.specialDateMultiplier || 1,
                    emergencyWhatsappNumber: initialData.emergencyWhatsappNumber || "",
                    emergencyDays: initialData.emergencyDays ? { id: Number(initialData.emergencyDays.id) } : { id: 1 },
                    emergencyTimeSlot: initialData.emergencyTimeSlot ? { id: Number(initialData.emergencyTimeSlot.id) } : { id: 1 },
                    serviceArea: initialData.serviceArea ? (typeof initialData.serviceArea === "object" ? initialData.serviceArea.id : initialData.serviceArea) : "",
                },
                equipment: {
                    equipmentTypeVal: initialData.equipmentTypeVal || "",
                    equipmentBrand: initialData.equipmentBrand || "",
                    equipmentModel: initialData.equipmentModel || "",
                    equipmentPower: initialData.equipmentPower || "",
                    equipmentYear: Number(initialData.equipmentYear) || 0,
                    otherEquipmentType: initialData.otherEquipmentType || "",
                },
                media: {
                    photos: initialData.photos || [],
                    videos: initialData.videos || [],
                    liveCaptures: initialData.liveCaptures || [],
                },
                files: initialData.files || [],
            };
        }

        // Default initial state
        return {
            description: "",
            requestType: "",
            serviceType: { id: 1 },
            projectTimeline: "",
            deadlineType: { id: 1 },
            projectScope: {
                description: "",
                timeline: "",
                budget: {
                    type: "fixed",
                    amount: "",
                    currency: "USD",
                },
            },
            budgetType: { id: 1 },
            budget: null,
            currency: { id: "USD" },
            title: "",
            contactName: "",
            contactPhone: "",
            contactEmail: "",
            canContactByWhatsapp: false,
            contactWhatsapp: "",
            address: {
                street: "",
                address: "",
                city: "",
                province: "",
                municipality: "",
                postalcode: "",
                floor: null,
                isElevatorAvailable: false,
                isStairsRequired: false,
                propertyType: { id: 1 },
            },
            emergency: {
                isEmergency: false,
                calculatedCost: 0,
                baseRate: 0,
                timeMultiplier: 1,
                dayMultiplier: 1,
                specialDateMultiplier: 1,
                emergencyWhatsappNumber: "",
                emergencyDays: { id: 1 },
                emergencyTimeSlot: { id: 1 },
                serviceArea: "",
            },
            equipment: {
                equipmentTypeVal: "",
                equipmentBrand: "",
                equipmentModel: "",
                equipmentPower: "",
                equipmentYear: null,
                otherEquipmentType: "",
            },
            media: {
                photos: [],
                videos: [],
                liveCaptures: [],
            },
            files: [],
        };
    };

    // Initial state with specific request types and cost calculation
    const [formData, setFormData] = useState<any>(getInitialFormData());

    // Update form data when initialData changes in edit mode
    useEffect(() => {
        if (isEditMode && initialData && initialData.id) {
            const newFormData = getInitialFormData();
            console.log("Updating form data with initial data:", newFormData, initialData.files);
            setFormData(newFormData);

            // Handle existing media files for edit mode
            if (initialData.files && Array.isArray(initialData.files)) {
                const existingFiles = initialData.files.map((file: any) => {
                    // Extract filename and type from file.path URL
                    let fileName = file.name || file.originalName || file.fileName;
                    let fileType = file.type || file.mimeType || file.contentType;

                    if (file.path) {
                        try {
                            // Extract filename from URL path
                            const urlPath = file.path;
                            const pathParts = urlPath.split("/");
                            const fullFileName = pathParts[pathParts.length - 1]; // Get the last part of the URL

                            if (fullFileName && fullFileName.includes(".")) {
                                // Extract filename without extension
                                const fileNameParts = fullFileName.split(".");
                                const extension = fileNameParts[fileNameParts.length - 1].toLowerCase();
                                fileName = fileNameParts.slice(0, -1).join(".") || fullFileName;

                                // Determine MIME type from extension
                                if (!fileType) {
                                    const mimeTypes: { [key: string]: string } = {
                                        "jpg": "image/jpeg",
                                        "jpeg": "image/jpeg",
                                        "png": "image/png",
                                        "gif": "image/gif",
                                        "bmp": "image/bmp",
                                        "webp": "image/webp",
                                        "svg": "image/svg+xml",
                                        "mp4": "video/mp4",
                                        "webm": "video/webm",
                                        "mov": "video/quicktime",
                                        "avi": "video/x-msvideo",
                                        "mkv": "video/x-matroska",
                                        "flv": "video/x-flv",
                                        "wmv": "video/x-ms-wmv",
                                        "m4v": "video/x-m4v",
                                        "3gp": "video/3gpp",
                                        "ogv": "video/ogg"
                                    };
                                    fileType = mimeTypes[extension] || "application/octet-stream";
                                }
                            }
                        } catch (error) {
                            console.error("Error parsing file path:", error);
                        }
                    }

                    return {
                        id: file.id,
                        name: fileName || `File ${file.id}`,
                        url: file.path || file.url || file.thumbnail || file.filePath || file.fileUrl || "",
                        type: fileType || "application/octet-stream"
                    };
                });
                setUploadedFiles(existingFiles);
                console.log("Set existing uploaded files from files array:", existingFiles);
            }
        }
    }, [isEditMode, initialData?.id]);

    const { user, currencies } = useSelector((state: any) => state.auth);
    const { typeCategories } = useSelector((state: any) => state.client);
    const { serviceAreas } = useSelector((state: any) => state.serviceArea);
    const { settings: categoryFormSettings } = useSelector((state: any) => state.categoryFormSettings);
    const { countries } = useSelector((state: any) => state.admin);

    // Selected currency state (separate from formData to prevent re-render issues)
    const [selectedCurrency, setSelectedCurrency] = useState<{id: string; name: string; code: string} | null>(null);
    const currencyDetectionRunRef = useRef(false);
    
    // Sync selectedCurrency with formData.currency ONLY when it actually changes
    useEffect(() => {
        if (formData.currency?.id && currencies && currencies.length > 0) {
            // Only update if the ID is different from current selectedCurrency
            if (selectedCurrency?.id !== formData.currency.id) {
                const currency = currencies.find((c: any) => c.value === formData.currency.id);
                if (currency) {
                    setSelectedCurrency({
                        id: formData.currency.id,
                        name: currency.label || "USD",
                        code: currency.initial || "USD"
                    });
                }
            }
        }
    }, [formData.currency?.id, currencies, selectedCurrency?.id]); // Add selectedCurrency.id to prevent warning

    // Handle currency selection
    const handleCurrencySelect = (currency: {id: string; name: string; code: string} | null) => {
        if (currency) {
            setSelectedCurrency(currency);
            handleFieldChange("currency", { id: currency.id });
        }
    };

    // Function to detect user location and set currency using centralized utility
    const detectUserLocationAndSetCurrency = async () => {
        if (!currencies || currencies.length === 0) {
            return;
        }

        try {
            // Use the centralized currency detection utility with Redux dispatch
            const result = await detectUserCurrency(currencies, dispatch);
            
            // Update form data with detected currency
            setFormData(prev => ({
                ...prev,
                currency: { id: result.currencyId || prev.currency?.id }
            }));
        } catch (error) {
            console.error("Error detecting currency:", error);
            // Fallback to USD
            const usdId = utilFindCurrencyIdByCode("USD", currencies);
            if (usdId) {
                setFormData(prev => ({
                    ...prev,
                    currency: { id: usdId }
                }));
            }
        }
    };

    const dispatch = useAppDispatch();
    const isLoading = useSelector(selectLoaderValue);

    // Helper functions to convert string values to numeric IDs
    const getPropertyTypeId = (propertyType: string): number => {
        switch (propertyType) {
            case "Residential": return 1;
            case "Commercial": return 2;
            case "Industrial": return 3;
            default: return 1; // Default to Residential
        }
    };

    const getServiceTypeId = (serviceType: string): number => {
        switch (serviceType) {
            case "Assistance": return 1;
            case "Repair": return 2;
            case "Replacement": return 3;
            default: return 1; // Default to Assistance
        }
    };

    const getBudgetTypeId = (budgetType: string): number => {
        switch (budgetType) {
            case "Fixed_Price": return 1;
            case "Hourly_Rate": return 2;
            default: return 1; // Default to Fixed_Price
        }
    };

    const getDeadlineTypeId = (deadlineType: string): number => {
        switch (deadlineType) {
            case "Information_Only": return 1;
            case "Within_A_Week": return 2;
            case "Within_A_Month": return 3;
            case "Future_Month": return 4;
            case "Is_Emergency": return 5;
            default: return 1; // Default to Information_Only
        }
    };



    // State for emergency service area search
    const [emergencySearchTerm, setEmergencySearchTerm] = useState("");
    const [filteredEmergencyAreas, setFilteredEmergencyAreas] = useState([]);
    const [showAutocompleteSuggestions, setShowAutocompleteSuggestions] = useState(false);
    const [isCreatingServiceArea, setIsCreatingServiceArea] = useState(false);
    const [autocompletePlace, setAutocompletePlace] = useState<any>(null);
    const [isGoogleMapsAvailable, setIsGoogleMapsAvailable] = useState(false);

    // Remove demo progress bar state - not needed for production

    // Emergency cost calculation using useMemo to prevent re-renders
    const emergencyCost = useMemo(() => {
        if (!formData.emergency.isEmergency) return 0;

        // Get settings from category form settings
        let settings = null;
        if (categoryFormSettings) {
            if (Array.isArray(categoryFormSettings) && categoryFormSettings.length > 0) {
                settings = categoryFormSettings[0];
            } else if (typeof categoryFormSettings === "object" && !Array.isArray(categoryFormSettings)) {
                settings = categoryFormSettings;
            }
        }

        if (!settings) return 80; // Default fallback

        // Get time slot and day type IDs
        const timeSlotId = Number(formData.emergency.emergencyTimeSlot.id);
        const dayTypeId = Number(formData.emergency.emergencyDays.id);

        // Determine the price based on time slot and day type
        let price = 80; // Default fallback

        if (timeSlotId === 1) { // Day (6 AM - 6 PM)
            if (dayTypeId === 1) { // Weekday
                price = settings.DayWeekdayPrice || 80;
            } else if (dayTypeId === 2) { // Weekend
                price = settings.DayWeekendPrice || 80;
            } else if (dayTypeId === 3) { // Holiday
                price = settings.DayHolidayPrice || 80;
            }
        } else if (timeSlotId === 2) { // Evening (6 PM - 10 PM)
            if (dayTypeId === 1) { // Weekday
                price = settings.EveningWeekdayPrice || 80;
            } else if (dayTypeId === 2) { // Weekend
                price = settings.EveningWeekendPrice || 80;
            } else if (dayTypeId === 3) { // Holiday
                price = settings.EveningHolidayPrice || 80;
            }
        } else if (timeSlotId === 3) { // Night (10 PM - 6 AM)
            if (dayTypeId === 1) { // Weekday
                price = settings.NightWeekdayPrice || 80;
            } else if (dayTypeId === 2) { // Weekend
                price = settings.NightWeekendPrice || 80;
            } else if (dayTypeId === 3) { // Holiday
                price = settings.NightHolidayPrice || 80;
            }
        }

        return price;
    }, [formData.emergency.isEmergency, formData.emergency.emergencyTimeSlot.id, formData.emergency.emergencyDays.id, categoryFormSettings]);

    // Update form data when emergency cost changes
    useEffect(() => {
        if (formData.emergency.isEmergency && formData.emergency.calculatedCost !== emergencyCost) {
            setFormData(prev => ({
                ...prev,
                emergency: {
                    ...prev.emergency,
                    calculatedCost: emergencyCost,
                },
            }));
        }
    }, [emergencyCost, formData.emergency.isEmergency]);

    // Media capture refs and state
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const emergencyAreaRef = useRef(null);
    const addressInputRef = useRef<HTMLInputElement>(null);
    const serviceAreaInputRef = useRef<HTMLInputElement>(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [capturedPhotos, setCapturedPhotos] = useState<string[]>([]);
    const [recordedVideo, setRecordedVideo] = useState<Blob | null>(null);

    // File upload state
    const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string, name: string, url: string, type: string }>>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadingFileName, setUploadingFileName] = useState("");
    const [totalUploadSize, setTotalUploadSize] = useState(0);
    const [uploadedSize, setUploadedSize] = useState(0);

    // Desktop camera section visibility
    const [showCameraSection, setShowCameraSection] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Helper functions to get accurate media counts
    const getCurrentImageCount = () => {
        return uploadedFiles.filter(f => {
            const isImageType = f.type && f.type.startsWith("image/");
            const isImageExtension = f.name && /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(f.name);
            return isImageType || isImageExtension;
        }).length;
    };

    const getCurrentVideoCount = () => {
        return uploadedFiles.filter(f => {
            const isVideoType = f.type && f.type.startsWith("video/");
            const isVideoExtension = f.name && /\.(mp4|webm|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)$/i.test(f.name);
            return isVideoType || isVideoExtension;
        }).length;
    };

    // Media capture functions
    const startCamera = async () => {
        try {
            // Check if MediaRecorder is supported
            if (!window.MediaRecorder) {
                GlobalService.showToast("error", t("common.error"), t("plumberForm.error.videoNotSupported"));
                return;
            }

            // Request camera with specific constraints for mobile
            const constraints = {
                video: {
                    facingMode: "environment", // Use rear camera on mobile
                    width: { ideal: 1280, max: 1920 },
                    height: { ideal: 720, max: 1080 },
                    frameRate: { ideal: 30, max: 30 } // Limit frame rate for better performance
                }
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                setIsCapturing(true);

                // Initialize MediaRecorder with better mobile support
                let mimeType = "video/webm";
                if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9")) {
                    mimeType = "video/webm;codecs=vp9";
                } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp8")) {
                    mimeType = "video/webm;codecs=vp8";
                } else if (MediaRecorder.isTypeSupported("video/mp4")) {
                    mimeType = "video/mp4";
                } else if (MediaRecorder.isTypeSupported("video/quicktime")) {
                    mimeType = "video/quicktime";
                }

                console.log("Using MIME type:", mimeType);

                const mediaRecorder = new MediaRecorder(stream, {
                    mimeType,
                    videoBitsPerSecond: 2500000 // 2.5 Mbps for better quality/size balance
                });
                mediaRecorderRef.current = mediaRecorder;
            }
        } catch (err) {
            console.error("Error accessing camera:", err);
            if (err.name === "NotAllowedError") {
                GlobalService.showToast("error", t("common.error"), t("plumberForm.error.cameraPermissionDenied"));
            } else if (err.name === "NotFoundError") {
                GlobalService.showToast("error", t("common.error"), t("plumberForm.error.cameraNotFound"));
            } else {
                GlobalService.showToast("error", t("common.error"), t("plumberForm.error.cameraError"));
            }
        }
    };

    const startVideoRecording = () => {
        // Check video limit (1 video max) - use helper function for accurate count
        const currentVideoCount = getCurrentVideoCount();

        if (currentVideoCount >= 1) {
            GlobalService.showToast("warning", t("common.warning"), t("plumberForm.error.videoLimitReached"));
            return;
        }

        if (mediaRecorderRef.current && !isRecording) {
            const mediaRecorder = mediaRecorderRef.current;
            const chunks: Blob[] = [];

            // Add error handling for MediaRecorder
            mediaRecorder.onerror = (event) => {
                console.error("MediaRecorder error:", event);
                GlobalService.showToast("error", t("common.error"), t("plumberForm.error.recordingError"));
                cleanupCamera();
            };

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    chunks.push(event.data);
                    console.log("Video chunk received, size:", event.data.size);
                }
            };

            mediaRecorder.onstop = () => {
                try {
                    const videoBlob = new Blob(chunks, { type: mediaRecorder.mimeType || "video/webm" });
                    console.log("Video recording stopped, blob size:", videoBlob.size, "MIME type:", videoBlob.type);

                    // Check file size (max 50MB for mobile)
                    const maxSize = 50 * 1024 * 1024; // 50MB
                    if (videoBlob.size > maxSize) {
                        GlobalService.showToast("warning", t("common.warning"), t("plumberForm.error.fileTooLarge"));
                    } else {
                        // Automatically upload the video
                        handleRecordedVideoUpload(videoBlob);
                    }
                } catch (error) {
                    console.error("Error creating video blob:", error);
                    GlobalService.showToast("error", t("common.error"), t("plumberForm.error.recordingError"));
                }
            };

            try {
                // Start recording with 1-second chunks for better mobile performance
                mediaRecorder.start(1000);
                setIsRecording(true);
                console.log("Video recording started");

                // Start recording timer (2 minutes = 120 seconds)
                timerRef.current = setInterval(() => {
                    setRecordingTime(prev => {
                        if (prev >= 120) {
                            stopVideoRecording();
                            return 120;
                        }
                        return prev + 1;
                    });
                }, 1000);
            } catch (error) {
                console.error("Error starting video recording:", error);
                GlobalService.showToast("error", t("common.error"), t("plumberForm.error.recordingError"));
                cleanupCamera();
            }
        }
    };

    const stopVideoRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            // Also stop the camera when stopping video recording
            cleanupCamera();
        }
    };

    const capturePhoto = () => {
        // Check image limit (5 images max) - use helper function for accurate count
        const currentImageCount = getCurrentImageCount();

        if (currentImageCount >= 5) {
            GlobalService.showToast("warning", t("common.warning"), t("plumberForm.error.imageLimitReached", { count: currentImageCount }));
            return;
        }

        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;

            // Set canvas dimensions to match video
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            const ctx = canvas.getContext("2d");
            if (ctx) {
                // Draw the current video frame to canvas
                ctx.drawImage(video, 0, 0);

                // Convert to blob for upload
                canvas.toBlob((blob) => {
                    if (blob) {
                        // Create a file from the blob
                        const file = new File([blob], `captured_photo_${Date.now()}.jpg`, { type: "image/jpeg" });

                        // Add to captured photos state
                        setCapturedPhotos(prev => [...prev, URL.createObjectURL(blob)]);

                        // Upload the captured photo
                        handleCapturedPhotoUpload(file);
                    }
                }, "image/jpeg", 0.8);
            }
        }
    };

    // Handle upload of captured photos
    const handleCapturedPhotoUpload = async (file: File) => {
        try {
            setIsUploading(true);
            setUploadingFileName(file.name);
            
            const fileSize = file.size;
            const chunkSize = 50 * 1024; // 50KB chunks
            const totalChunks = Math.ceil(fileSize / chunkSize);
            
            // Estimate upload time based on file size (assume ~300KB/s upload speed)
            const estimatedUploadSpeed = 300 * 1024; // 300KB per second
            const estimatedTimeInSeconds = fileSize / estimatedUploadSpeed;
            const estimatedTimeInMs = estimatedTimeInSeconds * 1000;
            
            // Calculate interval: total time / total chunks
            const intervalPerChunk = Math.max(Math.floor(estimatedTimeInMs / totalChunks), 50);
            
            let currentChunk = 0;

            // Simulate chunk-based progress with realistic timing
            const progressInterval = setInterval(() => {
                currentChunk++;
                const progress = Math.min((currentChunk / totalChunks) * 95, 95);
                setUploadProgress(progress);
                
                if (currentChunk >= totalChunks) {
                    clearInterval(progressInterval);
                }
            }, intervalPerChunk);

            const { payload } = await dispatch(uploadContractFile(file) as any);
            clearInterval(progressInterval);
            
            const fileId = payload?.file?.id;
            const fileUrl = payload?.file?.path;

            // Complete the progress
            setUploadProgress(100);

            // Keep progress visible briefly then reset
            setTimeout(() => {
                setUploadProgress(0);
                setUploadingFileName("");
                setIsUploading(false);
            }, 800); // Brief completion display

            if (fileId) {
                // Add to uploaded files
                setUploadedFiles(prev => [...prev, {
                    id: fileId,
                    name: file.name,
                    url: fileUrl || URL.createObjectURL(file),
                    type: file.type,
                }]);

                // Update form data files array
                setFormData(prev => ({
                    ...prev,
                    files: [...prev.files, { id: fileId }],
                    media: {
                        ...prev.media,
                        photos: [...prev.media.photos, file],
                    },
                }));

                GlobalService.showToast("success", t("common.success"), t("plumberForm.success.photoCaptured"));

                // Clean up capturedPhotos array after successful upload
                setCapturedPhotos([]);
            }
        } catch (error) {
            console.error("Error uploading captured photo:", error);
            GlobalService.showToast("error", t("common.error"), t("plumberForm.error.uploadFailed"));
            setUploadProgress(0);
            setUploadingFileName("");
            setIsUploading(false);
        }
    };

    // Handle upload of recorded video
    const handleRecordedVideoUpload = async (videoBlob: Blob) => {
        try {
            // Check file size before upload
            const maxSize = 50 * 1024 * 1024; // 50MB
            if (videoBlob.size > maxSize) {
                GlobalService.showToast("error", t("common.error"), t("plumberForm.error.fileTooLarge"));
                return;
            }

            setIsUploading(true);
            setUploadingFileName("Processing video...");

            // Create a file from the video blob with proper MIME type
            const mimeType = videoBlob.type || "video/webm";
            const fileExtension = mimeType.includes("mp4") ? "mp4" :
                mimeType.includes("quicktime") ? "mov" : "webm";
            const file = new File([videoBlob], `recorded_video_${Date.now()}.${fileExtension}`, { type: mimeType });

            const fileSize = file.size;
            const chunkSize = 50 * 1024; // 50KB chunks
            const totalChunks = Math.ceil(fileSize / chunkSize);
            
            // Estimate upload time based on file size (assume ~300KB/s upload speed)
            const estimatedUploadSpeed = 300 * 1024; // 300KB per second
            const estimatedTimeInSeconds = fileSize / estimatedUploadSpeed;
            const estimatedTimeInMs = estimatedTimeInSeconds * 1000;
            
            // Calculate interval: total time / total chunks
            const intervalPerChunk = Math.max(Math.floor(estimatedTimeInMs / totalChunks), 50);
            
            let currentChunk = 0;

            // Simulate chunk-based progress with realistic timing
            const progressInterval = setInterval(() => {
                currentChunk++;
                const progress = Math.min((currentChunk / totalChunks) * 95, 95);
                setUploadProgress(progress);
                
                if (currentChunk >= totalChunks) {
                    clearInterval(progressInterval);
                }
            }, intervalPerChunk);

            const { payload } = await dispatch(uploadContractFile(file) as any);
            clearInterval(progressInterval);
            
            const fileId = payload?.file?.id;
            const fileUrl = payload?.file?.path;

            // Complete the progress
            setUploadProgress(100);
            setUploadingFileName("Video uploaded successfully!");

            // Keep progress visible briefly then reset
            setTimeout(() => {
                setUploadProgress(0);
                setUploadingFileName("");
                setIsUploading(false);
            }, 1000); // Slightly longer for videos

            if (fileId) {
                // Add to uploaded files
                setUploadedFiles(prev => [...prev, {
                    id: fileId,
                    name: file.name,
                    url: fileUrl || URL.createObjectURL(videoBlob),
                    type: file.type,
                }]);

                // Update form data files array
                setFormData(prev => ({
                    ...prev,
                    files: [...prev.files, { id: fileId }],
                    media: {
                        ...prev.media,
                        videos: [...prev.media.videos, file],
                    },
                }));

                GlobalService.showToast("success", t("common.success"), t("plumberForm.success.videoUploaded"));
            } else {
                throw new Error("No file ID returned from upload");
            }
        } catch (error) {
            console.error("Error uploading recorded video:", error);

            // More specific error messages
            if (error.message?.includes("network")) {
                GlobalService.showToast("error", t("common.error"), t("plumberForm.error.networkError"));
            } else if (error.message?.includes("timeout")) {
                GlobalService.showToast("error", t("common.error"), t("plumberForm.error.uploadTimeout"));
            } else {
                GlobalService.showToast("error", t("common.error"), t("plumberForm.error.uploadFailed"));
            }

            setUploadProgress(0);
            setUploadingFileName("");
            setIsUploading(false);
        }
    };

    // File upload handler
    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        // Separate files by type for processing using improved detection
        const imageFiles = Array.from(files).filter(f => {
            const isImageType = f.type && f.type.startsWith("image/");
            const isImageExtension = f.name && /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(f.name);
            return isImageType || isImageExtension;
        });
        const videoFiles = Array.from(files).filter(f => {
            const isVideoType = f.type && f.type.startsWith("video/");
            const isVideoExtension = f.name && /\.(mp4|webm|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)$/i.test(f.name);
            return isVideoType || isVideoExtension;
        });

        // Check image limit (5 images max) - use helper function for accurate count
        const currentImageCount = getCurrentImageCount();
        if (currentImageCount + imageFiles.length > 5) {
            GlobalService.showToast("warning", t("common.warning"), t("plumberForm.error.imageLimitExceeded", { count: currentImageCount }));
            return;
        }

        // Check video limit (1 video max) - use helper function for accurate count
        const currentVideoCount = getCurrentVideoCount();
        if (currentVideoCount + videoFiles.length > 1) {
            GlobalService.showToast("warning", t("common.warning"), t("plumberForm.error.videoLimitExceeded"));
            return;
        }

        // Calculate total size of all files
        const filesArray = Array.from(files);
        const totalSize = filesArray.reduce((acc, file) => acc + file.size, 0);
        const chunkSize = 50 * 1024; // 50KB chunks
        const totalChunks = Math.ceil(totalSize / chunkSize);
        
        setIsUploading(true);
        setUploadingFileName("Starting upload...");
        setTotalUploadSize(totalSize);
        setUploadedSize(0);

        try {
            const newUploadedFiles: Array<{ id: string, name: string, url: string, type: string }> = [];
            let uploadedBytes = 0;

            for (let i = 0; i < filesArray.length; i++) {
                const file = filesArray[i];
                setUploadingFileName(`Uploading ${file.name} (${i + 1}/${filesArray.length})`);

                const fileSize = file.size;
                const fileChunks = Math.ceil(fileSize / chunkSize);
                
                // Estimate upload time for this file (assume ~300KB/s upload speed)
                const estimatedUploadSpeed = 300 * 1024; // 300KB per second
                const estimatedTimeInSeconds = fileSize / estimatedUploadSpeed;
                const estimatedTimeInMs = estimatedTimeInSeconds * 1000;
                
                // Calculate interval: estimated time / chunks for this file
                const intervalPerChunk = Math.max(Math.floor(estimatedTimeInMs / fileChunks), 50);
                
                let currentChunk = 0;

                // Simulate chunk-based progress for this file with realistic timing
                const progressInterval = setInterval(() => {
                    currentChunk++;
                    const bytesForThisChunk = Math.min(chunkSize, fileSize - (currentChunk - 1) * chunkSize);
                    uploadedBytes += bytesForThisChunk;
                    
                    const overallProgress = Math.min((uploadedBytes / totalSize) * 95, 95);
                    setUploadProgress(overallProgress);
                    setUploadedSize(uploadedBytes);
                    
                    if (currentChunk >= fileChunks) {
                        clearInterval(progressInterval);
                    }
                }, intervalPerChunk);

                const { payload } = await dispatch(uploadContractFile(file) as any);
                clearInterval(progressInterval);
                
                const fileId = payload?.file?.id;
                const fileUrl = payload?.file?.path;

                if (fileId) {
                    newUploadedFiles.push({
                        id: fileId,
                        name: file.name,
                        url: fileUrl || URL.createObjectURL(file),
                        type: file.type,
                    });
                }

                // Ensure we're at the right progress for this file
                uploadedBytes = Math.min(uploadedBytes, (i + 1) * (totalSize / filesArray.length));
                const fileProgress = ((i + 1) / filesArray.length) * 95;
                setUploadProgress(fileProgress);
            }

            // Complete the progress and keep it visible briefly
            setUploadProgress(100);
            setUploadingFileName(`Upload completed! (${formatFileSize(totalSize)})`);

            setTimeout(() => {
                setUploadProgress(0);
                setUploadingFileName("");
                setIsUploading(false);
                setTotalUploadSize(0);
                setUploadedSize(0);
            }, 1000);

            setUploadedFiles(prev => [...prev, ...newUploadedFiles]);

            // Update the form data files array with file IDs
            setFormData(prev => ({
                ...prev,
                files: [
                    ...prev.files,
                    ...newUploadedFiles.map(file => ({ id: file.id }))
                ],
                media: {
                    ...prev.media,
                    photos: [
                        ...prev.media.photos,
                        ...imageFiles,
                    ],
                    videos: [
                        ...prev.media.videos,
                        ...videoFiles,
                    ],
                },
            }));

            GlobalService.showToast("success", t("common.success"), t("plumberForm.success.filesUploaded", { count: newUploadedFiles.length }));
        } catch (error) {
            console.error("Error uploading files:", error);
            GlobalService.showToast("error", t("common.error"), t("plumberForm.error.uploadFailed"));
            setUploadProgress(0);
            setUploadingFileName("");
            setIsUploading(false);
            setTotalUploadSize(0);
            setUploadedSize(0);
        }
    };

    // Helper function to format file size
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    };

    // Remove uploaded file
    const handleRemoveUploadedFile = (fileId: string) => {
        // Find the file to determine its type
        const fileToRemove = uploadedFiles.find(file => file.id === fileId);

        setUploadedFiles(prev => prev.filter(file => file.id !== fileId));

        // Remove from form data files array and media arrays
        setFormData(prev => {
            const newFormData = {
                ...prev,
                files: prev.files.filter(file => file.id !== fileId),
                media: {
                    ...prev.media,
                    photos: [...prev.media.photos],
                    videos: [...prev.media.videos]
                }
            };

            // Check if it's an image using improved detection
            const isImage = fileToRemove && (
                (fileToRemove.type && fileToRemove.type.startsWith("image/")) ||
                (fileToRemove.name && /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(fileToRemove.name))
            );

            // Check if it's a video using improved detection
            const isVideo = fileToRemove && (
                (fileToRemove.type && fileToRemove.type.startsWith("video/")) ||
                (fileToRemove.name && /\.(mp4|webm|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)$/i.test(fileToRemove.name))
            );

            // If it's an image, remove one photo from the photos array
            if (isImage) {
                newFormData.media.photos.pop(); // Remove the last photo (most recently added)
            }

            // If it's a video, remove one video from the videos array
            if (isVideo) {
                newFormData.media.videos.pop(); // Remove the last video (most recently added)
            }

            return newFormData;
        });

        // Log for debugging
        console.log(`Removed file: ${fileToRemove?.name} (${fileToRemove?.type})`);
        console.log(`Updated uploadedFiles count: ${uploadedFiles.length - 1}`);
    };

    // Cleanup function for camera and recording
    const cleanupCamera = () => {
        // Stop all media tracks
        if (videoRef.current?.srcObject) {
            const stream = videoRef.current.srcObject as MediaStream;
            const tracks = stream.getTracks();
            tracks.forEach(track => {
                track.stop();
                console.log("Stopped track:", track.kind);
            });
            // Clear the srcObject to prevent flickering
            videoRef.current.srcObject = null;
        }

        // Stop MediaRecorder if it's recording
        if (mediaRecorderRef.current && isRecording) {
            try {
                mediaRecorderRef.current.stop();
            } catch (error) {
                console.log("MediaRecorder already stopped");
            }
        }

        // Clear the timer if it exists
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        // Reset all states
        setIsCapturing(false);
        setIsRecording(false);
        setRecordingTime(0);
        setCapturedPhotos([]); // Clear captured photos
        mediaRecorderRef.current = null;

        console.log("Camera cleanup completed");
    };
    const getCategoriesData = async () => {
        await dispatch(getTypeCategories({ id: TypeEnum.Home_Service }));
    };

    const fetchFormData = async () => {
        try {
            // Fetch currencies
            await dispatch(getAllCurrencies({ isShowLoader: false }));

            // Fetch service areas (first page with 50 items)
            await dispatch(getAllServiceAreas({ page: 1, limit: 50, isShowLoader: false }));

            // Fetch countries
            await dispatch(getAllCountries({ isShowLoader: false }));

            // Fetch categories
            await dispatch(getTypeCategories({ id: TypeEnum.Home_Service }));

            // Fetch category form settings
            await dispatch(fetchCategoryFormSettings());
        } catch (error) {
            console.error("Error fetching form data:", error);
        }
    };

    useEffect(() => {
        // Scroll to top when component mounts
        window.scrollTo({ top: 0, behavior: "smooth" });
        // Also try to scroll parent containers
        const scrollableParent = document.querySelector(".overflow-y-auto");
        if (scrollableParent) {
            scrollableParent.scrollTop = 0;
        }
        
        fetchFormData();

        // Check if mobile on mount
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 640);
        };

        checkMobile();
        window.addEventListener("resize", checkMobile);

        // Cleanup camera when component unmounts
        return () => {
            cleanupCamera();
            window.removeEventListener("resize", checkMobile);
        };
    }, []);

    // Auto-populate contact information from user data
    useEffect(() => {
        if (user && !isEditMode) {
            setFormData(prev => ({
                ...prev,
                contactName: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : prev.contactName,
                contactEmail: user.email || prev.contactEmail,
                contactPhone: user.phone || prev.contactPhone,
                contactWhatsapp: user.phone || prev.contactWhatsapp,
            }));
        }
    }, [user, isEditMode]);

    // Detect user location and set currency when currencies are loaded (RUN ONCE)
    useEffect(() => {
        if (currencies && currencies.length > 0 && !currencyDetectionRunRef.current) {
            currencyDetectionRunRef.current = true;
            
            // Normalize existing value if it's a code instead of backend id
            const currentId = formData.currency?.id;
            const isKnownId = currencies.some((c: any) => c.value === currentId);
            if (!isKnownId && typeof currentId === "string") {
                const mappedId = utilFindCurrencyIdByCode(currentId, currencies);
                if (mappedId) {
                    setFormData(prev => ({ ...prev, currency: { id: mappedId } }));
                }
            }
            // Then try location detection to refine (only once)
            detectUserLocationAndSetCurrency();
        }
    }, [currencies]); // Remove formData.currency?.id from dependencies to prevent loop

    // Filter emergency service areas based on search term using backend API
    useEffect(() => {
        if (emergencySearchTerm.trim() === "") {
            setFilteredEmergencyAreas([]);
            setShowAutocompleteSuggestions(false);
        } else {
            // Use backend search with keyword parameter
            const searchServiceAreas = async () => {
                try {
                    const result = await dispatch(getAllServiceAreas({
                        page: 1,
                        limit: 50,
                        search: emergencySearchTerm.trim(),
                        isShowLoader: false
                    }));

                    if (result.meta.requestStatus === "fulfilled" && result.payload) {
                        const filtered = result.payload.data || [];
                        setFilteredEmergencyAreas(filtered);

                        // If no service areas found, show autocomplete suggestions
                        if (filtered.length === 0 && emergencySearchTerm.trim().length > 2) {
                            setShowAutocompleteSuggestions(true);
                        } else {
                            setShowAutocompleteSuggestions(false);
                        }
                    } else {
                        setFilteredEmergencyAreas([]);
                        setShowAutocompleteSuggestions(false);
                    }
                } catch (error) {
                    console.error("Error searching service areas:", error);
                    setFilteredEmergencyAreas([]);
                    setShowAutocompleteSuggestions(false);
                }
            };

            // Add debounce to avoid too many API calls
            const timeoutId = setTimeout(searchServiceAreas, 300);
            return () => clearTimeout(timeoutId);
        }
    }, [emergencySearchTerm, dispatch]);

    // Function to clear autocomplete selection
    const clearAutocompleteSelection = () => {
        setAutocompletePlace(null);
        setEmergencySearchTerm("");
        setShowAutocompleteSuggestions(false);
    };

    // Function to create new service area from autocomplete selection
    const createServiceAreaFromPlace = async (place: any) => {
        if (!place || !place.geometry) {
            GlobalService.showToast("error", t("common.error"), t("plumberForm.error.invalidLocation"));
            return;
        }

        setIsCreatingServiceArea(true);
        try {
            // Extract location data using utility function
            const locationData: LocationData = extractLocationFromPlace(place);

            // Find or create country
            let countryId = "";
            if (locationData.country) {
                try {
                    countryId = await findOrCreateCountry(
                        locationData.country,
                        countries || [],
                        createCountry,
                        dispatch
                    );
                } catch (error) {
                    console.error("Error handling country:", error);
                    GlobalService.showToast("error", t("common.error"), t("plumberForm.error.countryError"));
                    setIsCreatingServiceArea(false);
                    return;
                }
            }

            // Prepare cities and postal codes arrays
            const cities = locationData.city ? [locationData.city] : [];
            let postalCodes = [];

            if (locationData.postalCode) {
                postalCodes = [locationData.postalCode];
            } else if (locationData.latitude && locationData.longitude && locationData.country) {
                // Generate a postal code as fallback
                const mockPostalCode = generateMockPostalCode(
                    locationData.latitude,
                    locationData.longitude,
                    locationData.country,
                    locationData.city
                );
                if (mockPostalCode) {
                    postalCodes = [mockPostalCode];
                }
            }

            // Create service area payload
            const serviceAreaPayload = {
                name: place.formatted_address || locationData.city || "New Service Area",
                type: { id: 2 }, // City type
                radius: 50, // Default radius
                restrictions: [],
                cities: cities,
                postalCodes: postalCodes,
                country: { id: countryId },
                serviceTypes: ["Emergency Services"],
                emergencyServices: false,
                province: locationData.province || "",
                latitude: locationData.latitude,
                longitude: locationData.longitude
            };

            // Create the service area
            const createResult = await dispatch(createServiceArea({ data: serviceAreaPayload }));

            if (createResult.meta.requestStatus === "fulfilled") {
                const newServiceArea = createResult.payload;

                // Update form data with the new service area ID
                setFormData(prev => ({
                    ...prev,
                    emergency: {
                        ...prev.emergency,
                        serviceArea: newServiceArea.id
                    }
                }));

                // Clear search and show success
                setEmergencySearchTerm("");
                setShowAutocompleteSuggestions(false);
                setAutocompletePlace(null);

                GlobalService.showToast("success", t("common.success"), t("plumberForm.success.serviceAreaCreated"));

                // Refresh service areas list
                dispatch(getAllServiceAreas({ page: 1, limit: 50, isShowLoader: false }));
            } else {
                throw new Error("Failed to create service area");
            }
        } catch (error) {
            console.error("Error creating service area:", error);
            GlobalService.showToast("error", t("common.error"), t("plumberForm.error.serviceAreaCreationFailed"));
        } finally {
            setIsCreatingServiceArea(false);
        }
    };

    // Handle clicking outside emergency area dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (emergencyAreaRef.current && !(emergencyAreaRef.current as any).contains(event.target)) {
                setEmergencySearchTerm("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Google Maps Autocomplete functionality
    useEffect(() => {
        // Load Google Maps script if not already loaded
        if (!window.google || !window.google.maps) {
            const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
            if (!apiKey) {
                console.warn("Google Maps API key not found. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your environment variables.");
                return;
            }

            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
            script.async = true;
            script.defer = true;
            script.onload = () => {
                console.log("Google Maps script loaded successfully");
                setIsGoogleMapsAvailable(true);
                initAutocomplete();
                initServiceAreaAutocomplete();
            };
            script.onerror = () => {
                console.error("Failed to load Google Maps script");
                setIsGoogleMapsAvailable(false);
            };
            document.head.appendChild(script);
        } else {
            console.log("Google Maps already available");
            setIsGoogleMapsAvailable(true);
            initAutocomplete();
            initServiceAreaAutocomplete();
        }

        return () => {
            // Cleanup if needed
        };
    }, []);

    // Initialize service area autocomplete when ref is available
    useEffect(() => {
        if (serviceAreaInputRef.current && window.google && window.google.maps) {
            initServiceAreaAutocomplete();
        }
    }, [serviceAreaInputRef.current, window.google]);

    const initAutocomplete = () => {
        if (!addressInputRef.current || !window.google || !window.google.maps) return;

        const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
            types: ["geocode", "establishment"],
            componentRestrictions: { country: [] },
            fields: ["address_components", "formatted_address", "geometry", "name"]
        });

        autocomplete.addListener("place_changed", () => {
            const place = autocomplete.getPlace();
            console.log("Selected place:", place); // Debug log

            if (place.geometry) {
                // Extract address components
                let streetNumber = "";
                let route = "";
                let city = "";
                let province = "";
                let municipality = "";
                let postalCode = "";
                let sublocality = "";
                let country = "";
                const fullAddress = place.formatted_address || "";

                place.address_components?.forEach((component) => {
                    const types = component.types;
                    console.log("Component:", component.long_name, "Types:", types); // Debug log

                    if (types.includes("street_number")) {
                        streetNumber = component.long_name;
                    } else if (types.includes("route")) {
                        route = component.long_name;
                    } else if (types.includes("locality")) {
                        city = component.long_name;
                    } else if (types.includes("sublocality")) {
                        sublocality = component.long_name;
                    } else if (types.includes("administrative_area_level_1")) {
                        municipality = component.long_name;
                    } else if (types.includes("administrative_area_level_2")) {
                        province = component.long_name;
                    } else if (types.includes("postal_code")) {
                        postalCode = component.long_name;
                    } else if (types.includes("postal_code_prefix")) {
                        // Fallback for some postal code formats
                        postalCode = component.long_name;
                    } else if (types.includes("postal_code_suffix")) {
                        // Additional postal code component
                        if (postalCode) {
                            postalCode += ` ${component.long_name}`;
                        } else {
                            postalCode = component.long_name;
                        }
                    } else if (types.includes("country")) {
                        country = component.long_name;
                    }
                });

                console.log("Extracted components:", { streetNumber, route, city, sublocality, province, municipality, postalCode, country }); // Debug log

                // Fallback: Try to extract postal code from formatted address if not found in components
                if (!postalCode && fullAddress) {
                    const postalCodeMatch = fullAddress.match(/\b[A-Z]\d[A-Z]\s?\d[A-Z]\d\b|\b\d{5}(?:-\d{4})?\b/);
                    if (postalCodeMatch) {
                        postalCode = postalCodeMatch[0];
                        console.log("Extracted postal code from formatted address:", postalCode);
                    }
                }

                // Fallback: Try to extract street name from formatted address if route is not found
                if (!route && fullAddress) {
                    // Try to extract street name from the beginning of the address
                    const addressParts = fullAddress.split(",");
                    if (addressParts.length > 0) {
                        const firstPart = addressParts[0].trim();
                        // Remove street number if present
                        const streetNameMatch = firstPart.match(/^\d+\s+(.+)$/);
                        if (streetNameMatch) {
                            route = streetNameMatch[1];
                            console.log("Extracted street name from formatted address:", route);
                        }
                    }
                }

                // Update form data with autocomplete results
                setFormData(prev => ({
                    ...prev,
                    address: {
                        ...prev.address,
                        address: streetNumber || (route ? `${route}` : fullAddress), // Use street number, or route, or full address
                        street: route || "", // Ensure street name is populated
                        city: city || "",
                        province: province || "",
                        municipality: municipality || "",
                        postalcode: postalCode || "", // Ensure postal code is populated
                    },
                }));

                console.log("Updated form data:", {
                    address: streetNumber || (route ? `${route}` : fullAddress),
                    street: route,
                    city: city,
                    sublocality: sublocality,
                    province: province,
                    municipality: municipality,
                    postalcode: postalCode,
                    country: country
                });
            }
        });
    };

    // Initialize Google Maps Autocomplete for service areas
    const initServiceAreaAutocomplete = () => {
        console.log("Initializing service area autocomplete...");
        console.log("Google Maps available:", !!window.google);
        console.log("Service area input ref:", !!serviceAreaInputRef.current);

        if (!window.google || !window.google.maps || !serviceAreaInputRef.current) {
            console.log("Service area autocomplete initialization failed - missing dependencies");
            return;
        }

        console.log("Creating service area autocomplete instance...");
        // Create autocomplete instance for the emergency search input
        const autocomplete = new window.google.maps.places.Autocomplete(serviceAreaInputRef.current, {
            types: ["geocode", "establishment"],
            componentRestrictions: { country: [] },
            fields: ["address_components", "formatted_address", "geometry", "name"]
        });

        autocomplete.addListener("place_changed", () => {
            const place = autocomplete.getPlace();
            console.log("Service area place selected:", place);
            if (place.geometry) {
                setAutocompletePlace(place);
                setEmergencySearchTerm(place.formatted_address || "");
            }
        });

        console.log("Service area autocomplete initialized successfully");
    };

    // Validation functions
    const validateField = (name: string, value: any): string => {
        switch (name) {
            case "title":
                if (!value || value.trim() === "") return "Title is required";
                if (value.trim().length < 10) return "Title must be at least 10 characters";
                if (value.trim().length > 70) return "Title must not exceed 70 characters";
                return "";

            case "description":
                if (!value || value.trim() === "") return "Description is required";
                if (value.trim().length < 15) return "Description must be at least 15 characters";
                if (value.trim().length > 600) return "Description must not exceed 600 characters";
                return "";

            case "contactName":
                if (!value || value.trim() === "") return "Full name is required";
                if (value.trim().length < 2) return "Name must be at least 2 characters";
                return "";

            case "contactEmail": {
                if (!value || value.trim() === "") return "Email is required";
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) return "Please enter a valid email address";
                return "";
            }

            case "contactPhone": {
                if (!value || value.trim() === "") return "Phone number is required";
                const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
                if (!phoneRegex.test(value.replace(/[\s\-()]/g, ""))) return "Please enter a valid phone number";
                return "";
            }

            case "contactWhatsapp": {
                if (formData.canContactByWhatsapp && (!value || value.trim() === "")) {
                    return "WhatsApp number is required when WhatsApp contact is enabled";
                }
                if (formData.canContactByWhatsapp && value) {
                    const whatsappRegex = /^[+]?[1-9][\d]{0,15}$/;
                    if (!whatsappRegex.test(value.replace(/[\s\-()]/g, ""))) return "Please enter a valid WhatsApp number";
                }
                return "";
            }

            case "budget":
                if (!value || value === null || value === undefined) return "Budget amount is required";
                if (value <= 0) return "Budget amount must be greater than 0";
                return "";

            case "currency":
                if (!value || !value.id) return "Currency is required";
                return "";

            case "propertyType":
                if (!value || !value.id) return "Property type is required";
                return "";

            case "address":
                if (!value || value.trim() === "") return "House/Building number is required";
                return "";

            case "street":
                if (!value || value.trim() === "") return "Street name is required";
                return "";

            case "city":
                if (!value || value.trim() === "") return "City is required";
                return "";

            case "province":
                if (!value || value.trim() === "") return "Province is required";
                return "";

            case "municipality":
                if (!value || value.trim() === "") return "Region is required";
                return "";

            case "postalcode":
                if (!value || value.trim() === "") return "Postcode is required";
                return "";

            case "serviceType":
                if (!value || !value.id) return "Service type is required";
                return "";

            case "budgetType":
                if (!value || !value.id) return "Budget type is required";
                return "";

            case "emergencyWhatsappNumber": {
                if (formData.emergency.isEmergency && (!value || value.trim() === "")) {
                    return "Emergency WhatsApp number is required for emergency services";
                }
                if (formData.emergency.isEmergency && value) {
                    const emergencyWhatsappRegex = /^[+]?[1-9][\d]{0,15}$/;
                    if (!emergencyWhatsappRegex.test(value.replace(/[\s\-()]/g, ""))) {
                        return "Please enter a valid emergency WhatsApp number";
                    }
                }
                return "";
            }

            case "emergencyTimeSlot":
                if (formData.emergency.isEmergency && (!value || !value.id)) {
                    return "Emergency time slot is required for emergency services";
                }
                return "";

            case "emergencyDays":
                if (formData.emergency.isEmergency && (!value || !value.id)) {
                    return "Emergency days selection is required for emergency services";
                }
                return "";

            case "serviceArea":
                if (formData.emergency.isEmergency && (!value || value === "")) {
                    return "Service area is required for emergency services";
                }
                return "";

            case "equipmentTypeVal":
                if ((formData.requestType === "repair" || formData.requestType === "replacement" ||
                    (formData.serviceType?.id && (Number(formData.serviceType.id) === 2 || Number(formData.serviceType.id) === 3))) &&
                    (!value || value === "")) {
                    return "Equipment type is required for repair or replacement services";
                }
                return "";

            case "equipmentBrand":
                // Optional field - no validation required
                return "";

            case "equipmentModel":
                // Optional field - no validation required
                return "";

            case "equipmentPower":
                // Optional field - no validation required
                return "";

            case "equipmentYear":
                // Optional field - no validation required
                return "";

            case "otherEquipmentType":
                if (formData.equipment.equipmentTypeVal === "OTHER" && (!value || value.trim() === "")) {
                    return "Please specify the equipment type";
                }
                return "";

            case "acceptTerms":
                if (!isEditMode && !value) {
                    return "You must accept the terms and conditions to proceed";
                }
                return "";

            default:
                return "";
        }
    };

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        // Basic required fields
        newErrors.title = validateField("title", formData.title);
        newErrors.description = validateField("description", formData.description);
        newErrors.contactName = validateField("contactName", formData.contactName);
        newErrors.contactEmail = validateField("contactEmail", formData.contactEmail);
        newErrors.contactPhone = validateField("contactPhone", formData.contactPhone);
        newErrors.contactWhatsapp = validateField("contactWhatsapp", formData.contactWhatsapp);
        newErrors.budget = validateField("budget", formData.budget);
        newErrors.currency = validateField("currency", formData.currency);
        newErrors.propertyType = validateField("propertyType", formData.address.propertyType);
        newErrors.address = validateField("address", formData.address.address);
        newErrors.street = validateField("street", formData.address.street);
        newErrors.city = validateField("city", formData.address.city);
        newErrors.province = validateField("province", formData.address.province);
        newErrors.municipality = validateField("municipality", formData.address.municipality);
        newErrors.postalcode = validateField("postalcode", formData.address.postalcode);
        newErrors.serviceType = validateField("serviceType", formData.serviceType);
        newErrors.budgetType = validateField("budgetType", formData.budgetType);

        // Emergency fields
        newErrors.emergencyWhatsappNumber = validateField("emergencyWhatsappNumber", formData.emergency.emergencyWhatsappNumber);
        newErrors.emergencyTimeSlot = validateField("emergencyTimeSlot", formData.emergency.emergencyTimeSlot);
        newErrors.emergencyDays = validateField("emergencyDays", formData.emergency.emergencyDays);
        newErrors.serviceArea = validateField("serviceArea", formData.emergency.serviceArea);

        // Equipment fields
        newErrors.equipmentTypeVal = validateField("equipmentTypeVal", formData.equipment.equipmentTypeVal);
        newErrors.equipmentBrand = validateField("equipmentBrand", formData.equipment.equipmentBrand);
        newErrors.equipmentModel = validateField("equipmentModel", formData.equipment.equipmentModel);
        newErrors.equipmentPower = validateField("equipmentPower", formData.equipment.equipmentPower);
        newErrors.equipmentYear = validateField("equipmentYear", formData.equipment.equipmentYear);
        newErrors.otherEquipmentType = validateField("otherEquipmentType", formData.equipment.otherEquipmentType);

        // Terms and conditions (only for create mode)
        if (!isEditMode) {
            newErrors.acceptTerms = validateField("acceptTerms", acceptTerms);
        }

        setErrors(newErrors);

        // Check if there are any errors
        return !Object.values(newErrors).some(error => error !== "");
    };

    const handleFieldChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const handleAddressChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            address: {
                ...prev.address,
                [field]: value
            }
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const handleEmergencyChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            emergency: {
                ...prev.emergency,
                [field]: value
            }
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const handleEquipmentChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            equipment: {
                ...prev.equipment,
                [field]: value
            }
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const handleFieldBlur = (field: string) => {
        setTouched(prev => ({
            ...prev,
            [field]: true
        }));

        // Validate field on blur
        const error = validateField(field, formData[field]);
        setErrors(prev => ({
            ...prev,
            [field]: error
        }));
    };

    const handleAddressBlur = (field: string) => {
        setTouched(prev => ({
            ...prev,
            [field]: true
        }));

        // Validate field on blur
        const error = validateField(field, formData.address[field]);
        setErrors(prev => ({
            ...prev,
            [field]: error
        }));
    };

    const handleEmergencyBlur = (field: string) => {
        setTouched(prev => ({
            ...prev,
            [field]: true
        }));

        // Validate field on blur
        const error = validateField(field, formData.emergency[field]);
        setErrors(prev => ({
            ...prev,
            [field]: error
        }));
    };

    const handleEquipmentBlur = (field: string) => {
        setTouched(prev => ({
            ...prev,
            [field]: true
        }));

        // Validate field on blur
        const error = validateField(field, formData.equipment[field]);
        setErrors(prev => ({
            ...prev,
            [field]: error
        }));
    };

    // Function to prepare form data for backend submission
    const prepareFormDataForSubmission = () => {
        const submissionData: any = {
            // Contact information matching backend properties
            contactName: formData.contactName,
            contactPhone: formData.contactPhone,
            contactEmail: formData.contactEmail,
            canContactByWhatsapp: formData.canContactByWhatsapp,
            contactWhatsapp: formData.contactWhatsapp,

            // Deadline type matching backend properties
            deadlineType: { id: Number(formData.deadlineType.id) },

            // Address fields matching backend properties
            propertyType: { id: Number(formData.address.propertyType.id) },
            address: formData.address.address,
            street: formData.address.street,
            city: formData.address.city,
            province: formData.address.province,
            municipality: formData.address.municipality,
            postalCode: Number(formData.address.postalcode) || 0,
            floor: Number(formData.address.floor) || 0,
            isStairsRequired: formData.address.isStairsRequired,
            isElevatorAvailable: formData.address.isElevatorAvailable,

            // Budget fields matching backend properties
            budgetType: { id: Number(formData.budgetType.id) },
            budget: Number(formData.budget) || 0,
            currency: { id: formData.currency.id },

            // Service type matching backend properties
            serviceType: { id: Number(formData.serviceType.id) },

            // Description field
            description: formData.description || "",

            // Other form fields
            title: formData.title,
            requestType: formData.requestType,
            projectTimeline: formData.projectTimeline,
            projectScope: formData.projectScope,
            equipmentYear: Number(formData.equipment.equipmentYear),
            equipmentPower: formData.equipment.equipmentPower,
            equipmentModel: formData.equipment.equipmentModel,
            equipmentTypeVal: formData.equipment.equipmentTypeVal === "OTHER" ? formData.equipment.otherEquipmentType : formData.equipment.equipmentTypeVal,
            equipmentBrand: formData.equipment.equipmentBrand,
            media: formData.media,
            files: formData.files,
        };

        // If this is an emergency, add emergency-specific fields
        if (formData.emergency.isEmergency) {
            submissionData.emergencyDays = { id: Number(formData.emergency.emergencyDays.id) };
            submissionData.emergencyTimeSlot = { id: Number(formData.emergency.emergencyTimeSlot.id) };
            submissionData.emergencyWhatsappNumber = formData.emergency.emergencyWhatsappNumber;
            submissionData.serviceArea = { id: formData.emergency.serviceArea };

            // Override contact WhatsApp with emergency WhatsApp
            if (formData.emergency.emergencyWhatsappNumber) {
                submissionData.contactWhatsapp = formData.emergency.emergencyWhatsappNumber;
                submissionData.canContactByWhatsapp = true;
            }
        }

        return submissionData;
    };
    return (

        <div
            className={`min-h-screen mb-16  bg-gray-50 ${isLoading ? "opacity-25" : "opacity-100"}`}
            style={{ textAlign: "left" }}>
            {/* <SharedHeader /> */}
            <div className="max-w-4xl mx-auto pt-6">

                <Card className="p-6">
                    <h2 className="text-xl font-semibold mb-4">{t("plumberForm.title")}</h2>
                    <div className="space-y-6">
                        {/* Title */}
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                {t("plumberForm.titleOfWork")}
                            </label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={e => {
                                    const value = e.target.value;
                                    if (value.length <= 70) {
                                        handleFieldChange("title", value);
                                    }
                                }}
                                onBlur={() => handleFieldBlur("title")}
                                className={`w-full px-4 py-2 border rounded-lg ${errors.title && (touched.title || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                placeholder={t("plumberForm.titlePlaceholder")}
                                maxLength={70}
                                required
                            />
                            <div className="flex justify-between items-center mt-1">
                                <div>
                                    {errors.title && (touched.title || isSubmitted) && (
                                        <p className="text-sm text-red-600">{errors.title}</p>
                                    )}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {formData.title.length}/70
                                </div>
                            </div>
                        </div>

                        {/* Detailed Description */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium">
                                    {t("plumberForm.detailedDescriptionLabel")}
                                </label>
                                <AIDescriptionGenerator
                                    title={formData.title}
                                    serviceType={formData.serviceType?.id === 1 ? "Assistance" : 
                                               formData.serviceType?.id === 2 ? "Repair" : 
                                               formData.serviceType?.id === 3 ? "Replacement" : undefined}
                                    requestType={formData.requestType}
                                    equipmentType={formData.equipment.equipmentTypeVal}
                                    propertyType={formData.address.propertyType?.id === 1 ? "Residential" :
                                                formData.address.propertyType?.id === 2 ? "Commercial" :
                                                formData.address.propertyType?.id === 3 ? "Industrial" : undefined}
                                    onDescriptionGenerated={(description) => {
                                        handleFieldChange("description", description);
                                        // Clear any existing description error
                                        if (errors.description) {
                                            setErrors(prev => ({
                                                ...prev,
                                                description: ""
                                            }));
                                        }
                                    }}
                                    disabled={isLoading}
                                    className="text-sm"
                                />
                            </div>
                            <textarea
                                value={formData.description}
                                onChange={e => handleFieldChange("description", e.target.value)}
                                onBlur={() => handleFieldBlur("description")}
                                className={`w-full px-4 py-3 border rounded-lg min-h-[100px] resize-y ${errors.description && (touched.description || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                placeholder={t("plumberForm.descriptionPlaceholder")}
                                required
                            />
                            {errors.description && (touched.description || isSubmitted) && (
                                <p className="text-sm text-red-600 mt-1">{errors.description}</p>
                            )}
                            <p className="text-sm text-gray-500">
                                {t("plumberForm.descriptionHelper")}
                            </p>
                        </div>
                    </div>
                </Card>
                <br></br>

                {/* Request Type Selection */}
                <Card className="p-6">
                    <h2 className="text-xl font-semibold mb-4">{t("plumberForm.whatTypeOfService")}</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <button
                            onClick={() => setFormData(prev => ({
                                ...prev,
                                requestType: "assistance",
                                serviceType: { id: 1 }
                            }))}
                            className={`p-4 rounded-lg border-2 transition-colors ${Number(formData.serviceType.id) === 1
                                ? "border-blue-500 bg-blue-50"
                                : "border-gray-200 hover:border-blue-200"
                                }`}>
                            <h3 className="font-medium">{t("plumberForm.requestAssistance")}</h3>
                            <p className="text-sm text-gray-500">{t("plumberForm.professionalConsultation")}</p>
                        </button>

                        <button
                            onClick={() => setFormData(prev => ({
                                ...prev,
                                requestType: "repair",
                                serviceType: { id: 2 }
                            }))}
                            className={`p-4 rounded-lg border-2 transition-colors ${Number(formData.serviceType.id) === 2
                                ? "border-blue-500 bg-blue-50"
                                : "border-gray-200 hover:border-blue-200"
                                }`}>
                            <h3 className="font-medium">{t("plumberForm.requestRepair")}</h3>
                            <p className="text-sm text-gray-500">{t("plumberForm.fixExistingIssues")}</p>
                        </button>

                        <button
                            onClick={() => setFormData(prev => ({
                                ...prev,
                                requestType: "replacement",
                                serviceType: { id: 3 }
                            }))}
                            className={`p-4 rounded-lg border-2 transition-colors ${Number(formData.serviceType.id) === 3
                                ? "border-blue-500 bg-blue-50"
                                : "border-gray-200 hover:border-blue-200"
                                }`}>
                            <h3 className="font-medium">{t("plumberForm.requestReplacement")}</h3>
                            <p className="text-sm text-gray-500">{t("plumberForm.installNewEquipment")}</p>
                        </button>
                    </div>
                </Card>
                <br></br>
                <Card className="p-6 overflow-visible relative z-10">
                    <h2 className="text-xl font-semibold mb-4">{t("plumberForm.budgetLabel")}</h2>
                    <div className="space-y-6 overflow-visible">
                        <div>
                            <label className="block text-sm font-medium mb-2">{t("plumberForm.budgetTypeLabel")}</label>
                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    onClick={() =>
                                        setFormData(prev => ({
                                            ...prev,
                                            budgetType: { id: 1 },
                                        }))
                                    }
                                    className={`p-4 rounded-lg border-2 transition-colors ${Number(formData.budgetType.id) === 1
                                        ? "border-blue-500 bg-blue-50"
                                        : "border-gray-200 hover:border-blue-200"
                                        }`}>
                                    <h3 className="font-medium">{t("plumberForm.fixedPrice")}</h3>
                                    <p className="text-sm text-gray-500">{t("plumberForm.payFixedAmount")}</p>
                                </button>
                                <button
                                    onClick={() =>
                                        setFormData(prev => ({
                                            ...prev,
                                            budgetType: { id: 2 },
                                        }))
                                    }
                                    className={`p-4 rounded-lg border-2 transition-colors ${Number(formData.budgetType.id) === 2
                                        ? "border-blue-500 bg-blue-50"
                                        : "border-gray-200 hover:border-blue-200"
                                        }`}>
                                    <h3 className="font-medium">{t("plumberForm.hourlyRateLabel")}</h3>
                                    <p className="text-sm text-gray-500">{t("plumberForm.payByTheHour")}</p>
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-visible">
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    {t("plumberForm.amountLabel")} <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-3 text-gray-500">
                                        {getCurrencySymbol(selectedCurrency?.code || "USD")}
                                    </span>
                                    <input
                                        type="number"
                                        value={formData.budget}
                                        onChange={e => handleFieldChange("budget", parseFloat(e.target.value) || 0)}
                                        onBlur={() => handleFieldBlur("budget")}
                                        className={`w-full pl-8 pr-4 py-2 border rounded-lg ${errors.budget && (touched.budget || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                        placeholder={
                                            formData.budgetType.id === 2
                                                ? t("plumberForm.hourlyRatePlaceholder")
                                                : t("plumberForm.totalBudgetPlaceholder")
                                        }
                                        required
                                    />
                                </div>
                                {errors.budget && (touched.budget || isSubmitted) && (
                                    <p className="text-sm text-red-600 mt-1">{errors.budget}</p>
                                )}
                            </div>

                            <div className="relative z-20">
                                <label className="block text-sm font-medium mb-2">
                                    {t("plumberForm.currencyLabel")} <span className="text-red-500">*</span>
                                </label>
                                <CurrencyDropdown
                                    selectedCurrency={selectedCurrency}
                                    onCurrencySelect={handleCurrencySelect}
                                    autoDetect={true}
                                    placeholder={t("plumberForm.selectCurrencyPlaceholder")}
                                />
                                {errors.currency && (touched.currency || isSubmitted) && (
                                    <p className="text-sm text-red-600 mt-1">{errors.currency}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </Card>
                {/* Service Type Selection */}
                {/* <Card className="p-6">
                        <h2 className="text-xl font-semibold mb-4">How would you like to proceed?</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" style={{ textAlign: "left" }}>
                            <button
                                onClick={() => setFormData(prev => ({ ...prev, serviceType: "quote" }))}
                                className={`p-4 rounded-lg border-2 transition-colors ${formData.serviceType === "quote"
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-200 hover:border-blue-200"
                                    }`}>
                                <h3 className="font-medium">Get Quote</h3>
                                <p className="text-sm text-gray-500">Receive professional quotes</p>
                            </button>

                            <button
                                onClick={() => setFormData(prev => ({ ...prev, serviceType: "hire" }))}
                                className={`p-4 rounded-lg border-2 transition-colors ${formData.serviceType === "hire"
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-200 hover:border-blue-200"
                                    }`}>
                                <h3 className="font-medium">Hire Direct</h3>
                                <p className="text-sm text-gray-500">Book a professional</p>
                            </button>

                            <button
                                onClick={() => setFormData(prev => ({ ...prev, serviceType: "both" }))}
                                className={`p-4 rounded-lg border-2 transition-colors ${formData.serviceType === "both"
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-200 hover:border-blue-200"
                                    }`}>
                                <h3 className="font-medium">Both Options</h3>
                                <p className="text-sm text-gray-500">Quote and hire option</p>
                            </button>
                        </div>
                    </Card> */}

                <br></br>

                {/* Property Information */}
                <Card className="p-6">
                    <h2 className="text-xl font-semibold mb-4">{t("plumberForm.propertyInformation")}</h2>
                    <div className="space-y-4" style={{ textAlign: "left" }}>
                        <div>
                            <label className="block text-sm font-medium mb-1">{t("plumberForm.propertyTypeLabel")}</label>
                            <select
                                value={formData.address.propertyType.id}
                                onChange={e => handleAddressChange("propertyType", { id: parseInt(e.target.value) })}
                                onBlur={() => handleAddressBlur("propertyType")}
                                className={`w-full px-4 py-2 border rounded-lg ${errors.propertyType && (touched.propertyType || isSubmitted) ? "border-red-500" : "border-gray-300"}`}>
                                <option value="">{t("plumberForm.selectPropertyTypePlaceholder")}</option>
                                <option value="1">{t("plumberForm.residential")}</option>
                                <option value="2">{t("plumberForm.commercial")}</option>
                                <option value="3">{t("plumberForm.industrial")}</option>
                            </select>
                            {errors.propertyType && (touched.propertyType || isSubmitted) && (
                                <p className="text-sm text-red-600 mt-1">{errors.propertyType}</p>
                            )}
                        </div>



                        {/* Address Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ textAlign: "left" }}>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("plumberForm.streetNameAndNumber")}</label>
                                <div className="relative">
                                    <input
                                        ref={addressInputRef}
                                        type="text"
                                        value={formData.address.street}
                                        onChange={e => handleAddressChange("street", e.target.value)}
                                        onBlur={() => handleAddressBlur("street")}
                                        className={`w-full px-4 py-2 pr-10 border rounded-lg ${errors.street && (touched.street || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                        placeholder={t("plumberForm.streetPlaceholder")}
                                    />
                                    <MapPin className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                </div>
                                {errors.street && (touched.street || isSubmitted) && (
                                    <p className="text-sm text-red-600 mt-1">{errors.street}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("plumberForm.houseBuildingNumber")}</label>
                                <input
                                    type="text"
                                    value={formData.address.address}
                                    onChange={e => handleAddressChange("address", e.target.value)}
                                    onBlur={() => handleAddressBlur("address")}
                                    className={`w-full px-4 py-2 border rounded-lg ${errors.address && (touched.address || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                    placeholder={t("plumberForm.houseBuildingPlaceholder")}
                                />
                                {errors.address && (touched.address || isSubmitted) && (
                                    <p className="text-sm text-red-600 mt-1">{errors.address}</p>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("plumberForm.cityLabel")}</label>
                                <input
                                    type="text"
                                    value={formData.address.city}
                                    onChange={e => handleAddressChange("city", e.target.value)}
                                    onBlur={() => handleAddressBlur("city")}
                                    className={`w-full px-4 py-2 border rounded-lg ${errors.city && (touched.city || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                    placeholder={t("plumberForm.cityPlaceholderText")}
                                />
                                {errors.city && (touched.city || isSubmitted) && (
                                    <p className="text-sm text-red-600 mt-1">{errors.city}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("plumberForm.regionDistrict")}</label>
                                <input
                                    type="text"
                                    value={formData.address.municipality}
                                    onChange={e => handleAddressChange("municipality", e.target.value)}
                                    onBlur={() => handleAddressBlur("municipality")}
                                    className={`w-full px-4 py-2 border rounded-lg ${errors.municipality && (touched.municipality || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                    placeholder={t("plumberForm.municipalityPlaceholder")}
                                />
                                {errors.municipality && (touched.municipality || isSubmitted) && (
                                    <p className="text-sm text-red-600 mt-1">{errors.municipality}</p>
                                )}
                            </div>

                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("plumberForm.provinceLabel")}</label>
                                <input
                                    type="text"
                                    value={formData.address.province}
                                    onChange={e => handleAddressChange("province", e.target.value)}
                                    onBlur={() => handleAddressBlur("province")}
                                    className={`w-full px-4 py-2 border rounded-lg ${errors.province && (touched.province || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                    placeholder={t("plumberForm.provincePlaceholder")}
                                />
                                {errors.province && (touched.province || isSubmitted) && (
                                    <p className="text-sm text-red-600 mt-1">{errors.province}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">{t("plumberForm.postcodeLabel")}</label>
                                <input
                                    type="text"
                                    value={formData.address.postalcode}
                                    onChange={e => handleAddressChange("postalcode", e.target.value)}
                                    onBlur={() => handleAddressBlur("postalcode")}
                                    className={`w-full px-4 py-2 border rounded-lg ${errors.postalcode && (touched.postalcode || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                />
                                {errors.postalcode && (touched.postalcode || isSubmitted) && (
                                    <p className="text-sm text-red-600 mt-1">{errors.postalcode}</p>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("plumberForm.floorLabel")}</label>
                                <input
                                    type="number"
                                    value={formData.address.floor}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            address: {
                                                ...prev.address,
                                                floor: parseInt(e.target.value) || 0,
                                            },
                                        }))
                                    }
                                    className="w-full px-4 py-2 border rounded-lg"
                                    placeholder={t("plumberForm.floorPlaceholder")}
                                />
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={formData.address.isElevatorAvailable}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            address: {
                                                ...prev.address,
                                                isElevatorAvailable: e.target.checked,
                                            },
                                        }))
                                    }
                                    className="h-4 w-4 text-blue-600 rounded"
                                />
                                <span>{t("plumberForm.elevatorAvailable")}</span>
                            </label>

                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={formData.address.isStairsRequired}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            address: {
                                                ...prev.address,
                                                isStairsRequired: e.target.checked,
                                            },
                                        }))
                                    }
                                    className="h-4 w-4 text-blue-600 rounded"
                                />
                                <span>{t("plumberForm.stairsRequired")}</span>
                            </label>
                        </div>
                    </div>
                </Card>
                <br></br>

                {/* Equipment Details */}
                {(formData.requestType === "repair" || formData.requestType === "replacement" ||
                    (formData.serviceType?.id && (Number(formData.serviceType.id) === 2 || Number(formData.serviceType.id) === 3))) && (
                        <Card className="p-6">
                            <h2 className="text-xl font-semibold mb-4">{t("plumberForm.equipmentDetails")}</h2>
                            <div className="space-y-4" style={{ textAlign: "left" }}>
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t("plumberForm.equipmentTypeLabel")}</label>
                                    <select
                                        value={formData.equipment.equipmentTypeVal}
                                        onChange={e => handleEquipmentChange("equipmentTypeVal", e.target.value)}
                                        onBlur={() => handleEquipmentBlur("equipmentTypeVal")}
                                        className={`w-full px-4 py-2 border rounded-lg ${errors.equipmentTypeVal && (touched.equipmentTypeVal || isSubmitted) ? "border-red-500" : "border-gray-300"}`}>
                                        <option value="">{t("plumberForm.selectEquipmentTypePlaceholder")}</option>
                                        <option value="Smartphones, tablets, and computers (PC/laptop)">{t("plumberForm.equipmentSmartphones")}</option>
                                        <option value="Furniture repairs">{t("plumberForm.equipmentFurniture")}</option>
                                        <option value="Washing machines, refrigerators, dishwashers, ovens, hobs, dryers, and freezers">{t("plumberForm.equipmentAppliances")}</option>
                                        <option value="Boilers">{t("plumberForm.equipmentBoilers")}</option>
                                        <option value="Air conditioners">{t("plumberForm.equipmentAirConditioners")}</option>
                                        <option value="Renewable Energy">{t("plumberForm.equipmentRenewableEnergy")}</option>
                                        <option value="Home Appliances">{t("plumberForm.equipmentHomeAppliances")}</option>
                                        <option value="OTHER">{t("plumberForm.equipmentOther")}</option>
                                    </select>
                                    {errors.equipmentTypeVal && (touched.equipmentTypeVal || isSubmitted) && (
                                        <p className="text-sm text-red-600 mt-1">{errors.equipmentTypeVal}</p>
                                    )}
                                </div>

                                {/* Other Equipment Type Input - Only show when "OTHER" is selected */}
                                {formData.equipment.equipmentTypeVal === "OTHER" && (
                                    <div>
                                        <label className="block text-sm font-medium mb-1">{t("plumberForm.specifyEquipmentType")}</label>
                                        <input
                                            type="text"
                                            value={formData.equipment.otherEquipmentType}
                                            onChange={e => handleEquipmentChange("otherEquipmentType", e.target.value)}
                                            onBlur={() => handleEquipmentBlur("otherEquipmentType")}
                                            className={`w-full px-4 py-2 border rounded-lg ${errors.otherEquipmentType && (touched.otherEquipmentType || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                            placeholder={t("plumberForm.specifyEquipmentPlaceholder")}
                                        />
                                        {errors.otherEquipmentType && (touched.otherEquipmentType || isSubmitted) && (
                                            <p className="text-sm text-red-600 mt-1">{errors.otherEquipmentType}</p>
                                        )}
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">{t("plumberForm.brandLabel")}</label>
                                        <input
                                            type="text"
                                            value={formData.equipment.equipmentBrand}
                                            onChange={e => handleEquipmentChange("equipmentBrand", e.target.value)}
                                            onBlur={() => handleEquipmentBlur("equipmentBrand")}
                                            className={`w-full px-4 py-2 border rounded-lg ${errors.equipmentBrand && (touched.equipmentBrand || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                            placeholder={t("plumberForm.brandPlaceholder")}
                                        />
                                        {errors.equipmentBrand && (touched.equipmentBrand || isSubmitted) && (
                                            <p className="text-sm text-red-600 mt-1">{errors.equipmentBrand}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">{t("plumberForm.modelLabel")}</label>
                                        <input
                                            type="text"
                                            value={formData.equipment.equipmentModel}
                                            onChange={e => handleEquipmentChange("equipmentModel", e.target.value)}
                                            onBlur={() => handleEquipmentBlur("equipmentModel")}
                                            className={`w-full px-4 py-2 border rounded-lg ${errors.equipmentModel && (touched.equipmentModel || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                            placeholder={t("plumberForm.modelPlaceholder")}
                                        />
                                        {errors.equipmentModel && (touched.equipmentModel || isSubmitted) && (
                                            <p className="text-sm text-red-600 mt-1">{errors.equipmentModel}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">{t("plumberForm.powerRating")}</label>
                                        <input
                                            type="text"
                                            value={formData.equipment.equipmentPower}
                                            onChange={e => handleEquipmentChange("equipmentPower", e.target.value)}
                                            onBlur={() => handleEquipmentBlur("equipmentPower")}
                                            className={`w-full px-4 py-2 border rounded-lg ${errors.equipmentPower && (touched.equipmentPower || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                            placeholder={t("plumberForm.powerPlaceholder")}
                                        />
                                        {errors.equipmentPower && (touched.equipmentPower || isSubmitted) && (
                                            <p className="text-sm text-red-600 mt-1">{errors.equipmentPower}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium mb-1">{t("plumberForm.yearOfInstallation")}</label>
                                        <input
                                            type="number"
                                            value={formData.equipment.equipmentYear}
                                            onChange={e => handleEquipmentChange("equipmentYear", parseInt(e.target.value) || 0)}
                                            onBlur={() => handleEquipmentBlur("equipmentYear")}
                                            className={`w-full px-4 py-2 border rounded-lg ${errors.equipmentYear && (touched.equipmentYear || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                            placeholder={t("plumberForm.yearPlaceholder")}
                                            min="1900"
                                            max={new Date().getFullYear()}
                                        />
                                        {errors.equipmentYear && (touched.equipmentYear || isSubmitted) && (
                                            <p className="text-sm text-red-600 mt-1">{errors.equipmentYear}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    )}
                <br></br>

                {/* Timeline Selection */}
                <Card className="p-6">
                    <h2 className="text-xl font-semibold mb-4">{t("plumberForm.whenDoYouNeed")}</h2>
                    <div className="space-y-4" style={{ textAlign: "left" }}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <button
                                onClick={() =>
                                    setFormData(prev => ({
                                        ...prev,
                                        projectTimeline: "Information_Only",
                                        deadlineType: { id: 1 },
                                    }))
                                }
                                className={`p-4 rounded-lg border-2 transition-colors ${Number(formData.deadlineType.id) === 1
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-200 hover:border-blue-200"
                                    }`}>
                                <h3 className="font-medium">{t("plumberForm.informationOnly")}</h3>
                                <p className="text-sm text-gray-500">{t("plumberForm.requestInformation")}</p>
                            </button>

                            <button
                                onClick={() =>
                                    setFormData(prev => ({
                                        ...prev,
                                        projectTimeline: "Within_A_Week",
                                        deadlineType: { id: 2 },
                                    }))
                                }
                                className={`p-4 rounded-lg border-2 transition-colors ${Number(formData.deadlineType.id) === 2
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-200 hover:border-blue-200"
                                    }`}>
                                <h3 className="font-medium">{t("plumberForm.withinAWeek")}</h3>
                                <p className="text-sm text-gray-500">{t("plumberForm.urgentNotEmergency")}</p>
                            </button>

                            <button
                                onClick={() =>
                                    setFormData(prev => ({
                                        ...prev,
                                        projectTimeline: "Within_A_Month",
                                        deadlineType: { id: 3 },
                                    }))
                                }
                                className={`p-4 rounded-lg border-2 transition-colors ${Number(formData.deadlineType.id) === 3
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-200 hover:border-blue-200"
                                    }`}>
                                <h3 className="font-medium">{t("plumberForm.withinAMonth")}</h3>
                                <p className="text-sm text-gray-500">{t("plumberForm.flexibleTimeline")}</p>
                            </button>

                            <button
                                onClick={() =>
                                    setFormData(prev => ({
                                        ...prev,
                                        projectTimeline: "Future_Month",
                                        deadlineType: { id: 4 },
                                    }))
                                }
                                className={`p-4 rounded-lg border-2 transition-colors ${Number(formData.deadlineType.id) === 4
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-200 hover:border-blue-200"
                                    }`}>
                                <h3 className="font-medium">{t("plumberForm.futureMonths")}</h3>
                                <p className="text-sm text-gray-500">{t("plumberForm.planningAhead")}</p>
                            </button>
                        </div>

                        {parentFormData?.urgency !== "normal" && <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={formData.emergency.isEmergency}
                                onChange={e => {
                                    setFormData(prev => ({
                                        ...prev,
                                        emergency: {
                                            ...prev.emergency,
                                            isEmergency: e.target.checked,
                                        },
                                    }));
                                    if (e.target.checked) {
                                        setFormData(prev => ({
                                            ...prev,
                                            projectTimeline: "Is_Emergency",
                                            deadlineType: { id: 5 },
                                        }));

                                    }
                                }}
                                className="h-4 w-4 text-red-600 rounded"
                            />
                            <span className="text-red-600 font-medium">{t("plumberForm.thisIsEmergency")}</span>
                        </div>}

                        {formData.emergency.isEmergency && (
                            <div className="mt-4 space-y-4" style={{ textAlign: "left" }}>
                                <div className="p-4 bg-red-50 rounded-lg space-y-4">
                                    <div className="flex items-center gap-2">
                                        <AlertCircle className="h-5 w-5 text-red-500" />
                                        <span className="text-red-700 font-medium">{t("plumberForm.emergencyServiceRequestLabel")}</span>
                                    </div>

                                    {/* Exit Fee Warning */}
                                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                        <p className="text-orange-600 text-xs sm:text-sm md:text-base leading-relaxed">
                                            {t("plumberForm.exitFeeWarning")}{" "}
                                            <span className="block sm:inline mt-1 sm:mt-0">
                                                {t("plumberForm.exitFeeVariants")}
                                            </span>
                                        </p>
                                    </div>

                                    <div ref={emergencyAreaRef} className="relative">
                                        <label className="block text-sm font-medium mb-2">
                                            {t("plumberForm.serviceAreaLabel")}
                                            {isCreatingServiceArea && (
                                                <span className="ml-2 text-sm text-blue-600">
                                                    {t("plumberForm.creatingServiceArea")}
                                                </span>
                                            )}
                                        </label>
                                        <div className="relative">
                                            <input
                                                ref={serviceAreaInputRef}
                                                type="text"
                                                placeholder={t("plumberForm.searchServiceAreasPlaceholder")}
                                                value={emergencySearchTerm}
                                                onChange={(e) => setEmergencySearchTerm(e.target.value)}
                                                onFocus={() => setEmergencySearchTerm("")}
                                                className="w-full px-3 sm:px-4 py-3 sm:py-2 border rounded-lg pr-10 text-base sm:text-sm"
                                            />
                                            {emergencySearchTerm ? (
                                                <button
                                                    type="button"
                                                    onClick={clearAutocompleteSelection}
                                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 hover:text-gray-600 touch-manipulation"
                                                    title={t("plumberForm.clearSelectionButton")}
                                                >
                                                    <X className="h-5 w-5" />
                                                </button>
                                            ) : (
                                                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            )}
                                        </div>

                                        {/* Google Maps API Status */}
                                        {!isGoogleMapsAvailable && (
                                            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                                                <p className="text-xs text-yellow-800">
                                                    <strong>{t("plumberForm.noteLabel")}</strong> {t("plumberForm.googleMapsNote")}
                                                </p>
                                            </div>
                                        )}

                                        {/* Dropdown Options */}
                                        {emergencySearchTerm && (
                                            <div className="absolute z-50 w-full sm:w-[90%] mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 sm:max-h-60 overflow-y-auto left-0 sm:left-auto bottom-auto sm:bottom-auto" style={{ maxHeight: "40vh" }}>
                                                {filteredEmergencyAreas.length > 0 ? (
                                                    // Show existing service areas
                                                    filteredEmergencyAreas.map((serviceArea: any) => {
                                                        const searchTerm = emergencySearchTerm.toLowerCase();
                                                        const matchingCities = Array.isArray(serviceArea.cities) ?
                                                            serviceArea.cities.filter((city: string) =>
                                                                city.toLowerCase().includes(searchTerm)
                                                            ) : [];
                                                        const matchingPostalCodes = Array.isArray(serviceArea.postalCodes) ?
                                                            serviceArea.postalCodes.filter((postalCode: string) =>
                                                                postalCode.toLowerCase().includes(searchTerm)
                                                            ) : [];

                                                        return (
                                                            <div
                                                                key={serviceArea.id}
                                                                onClick={() => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        emergency: { ...prev.emergency, serviceArea: serviceArea.id },
                                                                    }));
                                                                    setEmergencySearchTerm("");
                                                                    setShowAutocompleteSuggestions(false);
                                                                }}
                                                                className="px-3 sm:px-4 py-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 active:bg-gray-200 touch-manipulation"
                                                            >
                                                                <div className="font-medium text-gray-900 text-sm sm:text-base">
                                                                    {serviceArea.name} - {serviceArea.province}
                                                                </div>
                                                                {(matchingCities.length > 0 || matchingPostalCodes.length > 0) && (
                                                                    <div className="text-xs sm:text-sm text-gray-600 mt-1">
                                                                        {matchingCities.length > 0 && (
                                                                            <div className="mb-1">
                                                                                <span className="text-gray-500">{t("plumberForm.cities")} </span>
                                                                                {matchingCities.slice(0, 2).join(", ")}
                                                                                {matchingCities.length > 2 && ` ${t("plumberForm.moreItems", { count: matchingCities.length - 2 })}`}
                                                                            </div>
                                                                        )}
                                                                        {matchingPostalCodes.length > 0 && (
                                                                            <div>
                                                                                <span className="text-gray-500">{t("plumberForm.postalCodesLabel")} </span>
                                                                                {matchingPostalCodes.slice(0, 2).join(", ")}
                                                                                {matchingPostalCodes.length > 2 && ` ${t("plumberForm.moreItems", { count: matchingPostalCodes.length - 2 })}`}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                ) : showAutocompleteSuggestions ? (
                                                    // Show autocomplete suggestions when no service areas found
                                                    <div className="space-y-2 p-2 sm:p-1">
                                                        {/* Show selected place for creation */}
                                                        {autocompletePlace && (
                                                            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                                                    <div className="flex-1">
                                                                        <p className="text-xs sm:text-sm font-medium text-green-800">
                                                                            {t("plumberForm.selectedLocationLabel")}
                                                                        </p>
                                                                        <p className="text-xs sm:text-sm text-green-700 break-words">
                                                                            {autocompletePlace.formatted_address}
                                                                        </p>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <button
                                                                            onClick={clearAutocompleteSelection}
                                                                            className="p-1 text-gray-400 hover:text-gray-600 touch-manipulation"
                                                                            title={t("plumberForm.clearSelectionButton")}
                                                                        >
                                                                            <X className="h-4 w-4" />
                                                                        </button>
                                                                        <Button
                                                                            onClick={() => createServiceAreaFromPlace(autocompletePlace)}
                                                                            disabled={isCreatingServiceArea}
                                                                            className="px-2 sm:px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded whitespace-nowrap"
                                                                        >
                                                                            {isCreatingServiceArea ? (
                                                                                <div className="flex items-center gap-1">
                                                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                                                                    <span className="hidden sm:inline">{t("plumberForm.creatingButton")}</span>
                                                                                    <span className="sm:hidden">...</span>
                                                                                </div>
                                                                            ) : (
                                                                                <>
                                                                                    <span className="hidden sm:inline">{t("plumberForm.createServiceAreaButton")}</span>
                                                                                    <span className="sm:hidden">{t("plumberForm.createButton")}</span>
                                                                                </>
                                                                            )}
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="px-4 py-3 text-gray-500 text-center text-sm">
                                                        {t("plumberForm.noServiceAreasFound")}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Selected Value Display */}
                                        {formData.emergency.serviceArea && !emergencySearchTerm && (
                                            <div className="mt-2 p-3 sm:p-2 bg-gray-50 rounded border">
                                                <span className="text-sm sm:text-sm text-gray-700 break-words">
                                                    {serviceAreas && serviceAreas.data && serviceAreas.data.find((sa: any) => sa.id === formData.emergency.serviceArea)?.name} - {serviceAreas && serviceAreas.data && serviceAreas.data.find((sa: any) => sa.id === formData.emergency.serviceArea)?.province}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">
                                            {t("plumberForm.emergencyTimeSlotLabel")}
                                        </label>
                                        <select
                                            value={formData.emergency.emergencyTimeSlot.id || ""}
                                            onChange={e => {
                                                handleEmergencyChange("emergencyTimeSlot", { id: parseInt(e.target.value) });

                                            }}
                                            onBlur={() => handleEmergencyBlur("emergencyTimeSlot")}
                                            className={`w-full px-4 py-2 border rounded-lg ${errors.emergencyTimeSlot && (touched.emergencyTimeSlot || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                            required
                                        >
                                            <option value="">{t("plumberForm.selectEmergencyTimePlaceholder")}</option>
                                            <option value="1">{t("plumberForm.dayTime")}</option>
                                            <option value="2">{t("plumberForm.eveningTime")}</option>
                                            <option value="3">{t("plumberForm.nightTime")}</option>
                                        </select>
                                        {errors.emergencyTimeSlot && (touched.emergencyTimeSlot || isSubmitted) && (
                                            <p className="text-sm text-red-600 mt-1">{errors.emergencyTimeSlot}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">
                                            {t("plumberForm.selectAvailableDays")}
                                        </label>
                                        <select
                                            value={formData.emergency.emergencyDays.id || ""}
                                            onChange={e => {
                                                handleEmergencyChange("emergencyDays", { id: parseInt(e.target.value) });

                                            }}
                                            onBlur={() => handleEmergencyBlur("emergencyDays")}
                                            className={`w-full px-4 py-2 border rounded-lg ${errors.emergencyDays && (touched.emergencyDays || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                            required
                                        >
                                            <option value="">{t("plumberForm.selectDayPlaceholder")}</option>
                                            <option value="1">{t("plumberForm.weekday")}</option>
                                            <option value="2">{t("plumberForm.weekend")}</option>
                                            <option value="3">{t("plumberForm.holiday")}</option>
                                        </select>
                                        {errors.emergencyDays && (touched.emergencyDays || isSubmitted) && (
                                            <p className="text-sm text-red-600 mt-1">{errors.emergencyDays}</p>
                                        )}
                                    </div>

                                    {
                                        formData.emergency.emergencyTimeSlot.id &&
                                            formData.emergency.emergencyDays.id ? (
                                            <div className="flex justify-between items-center p-4 bg-white rounded-lg border border-red-200">
                                                <div>
                                                    <span className="text-gray-600">{t("plumberForm.emergencyServiceTerms")}</span>
                                                    <p className="text-base text-gray-700">
                                                        {t("plumberForm.emergencyTermsDetails")}
                                                    </p>
                                                    <p className="text-sm text-red-600 mt-1">{getDynamicCurrencySymbol()}{emergencyCost} {t("plumberForm.exitCost")}</p>
                                                </div>
                                                <Info className="h-5 w-5 text-gray-400" />
                                            </div>
                                        ) : null}

                                    <div>
                                        <label className="block text-sm font-medium mb-2">
                                            {t("plumberForm.whatsappRequiredForEmergency")}
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <span className="text-black-500 text-sm">+</span>
                                            </div>
                                            <input
                                                type="tel"
                                                value={(formData.emergency.emergencyWhatsappNumber || "").replace(/^\+/, "")}
                                                onChange={(e) => {
                                                    let value = e.target.value.replace(/\D/g, "");
                                                    if (value) {
                                                        value = "+" + value;
                                                    }
                                                    handleEmergencyChange("emergencyWhatsappNumber", value);
                                                }}
                                                placeholder={t("plumberForm.phoneNumberPlaceholder")}
                                                className={`w-full pl-8 pr-3 py-2 border rounded-lg ${errors.emergencyWhatsappNumber && (touched.emergencyWhatsappNumber || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                                required
                                            />
                                        </div>
                                        {errors.emergencyWhatsappNumber && (touched.emergencyWhatsappNumber || isSubmitted) && (
                                            <p className="text-sm text-red-600 mt-1">{errors.emergencyWhatsappNumber}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </Card>
                <br></br>

                {/* Image/Video Upload */}
                <Card className="p-6">
                    <h2 className="text-xl font-semibold mb-4">{t("plumberForm.photosAndVideos")}</h2>

                    <div className="space-y-6">
                        {/* File Upload */}
                        <div>
                            <label className="block text-sm font-medium mb-2">{t("plumberForm.uploadPhotosOrVideos")}</label>
                            <div className="flex items-center justify-center w-full">
                                <label className="w-full flex flex-col items-center px-4 py-6 bg-white rounded-lg border-2 border-dashed border-gray-300 cursor-pointer hover:bg-gray-50">
                                    {isUploading ? (
                                        <>
                                            <span className="mt-2 text-sm text-gray-500">{t("plumberForm.uploadingLabel")}</span>
                                            {uploadingFileName && (
                                                <span className="mt-1 text-xs text-gray-400">{uploadingFileName}</span>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="h-8 w-8 text-gray-400" />
                                            <span className="mt-2 text-sm text-gray-500">
                                                {t("plumberForm.clickToUpload")}
                                            </span>
                                        </>
                                    )}
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*,video/*"
                                        className="hidden"
                                        onChange={handleFileUpload}
                                        disabled={isUploading}
                                    />
                                </label>
                            </div>

                            {/* Upload Progress Bar - Only show when uploading */}
                            {isUploading && (
                                <div className="mt-4 space-y-2">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-gray-600 truncate max-w-[70%]">
                                            {uploadingFileName || t("plumberForm.uploadingLabel")}
                                        </span>
                                        <span className="text-gray-500 font-medium">{Math.round(uploadProgress)}%</span>
                                    </div>
                                    {totalUploadSize > 0 && (
                                        <div className="flex justify-between items-center text-xs text-gray-500">
                                            <span>{formatFileSize(uploadedSize)} / {formatFileSize(totalUploadSize)}</span>
                                            <span className="text-gray-400">
                                                {totalUploadSize > 1024 * 1024 ? "Large file" : t("plumberForm.uploadingLabel")}
                                            </span>
                                        </div>
                                    )}
                                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                        <div
                                            className="bg-blue-600 h-3 rounded-full transition-all duration-200 ease-linear"
                                            style={{ width: `${uploadProgress}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Display Uploaded Photos */}
                        {uploadedFiles.filter(f => {
                            const isImageType = f.type && f.type.startsWith("image/");
                            const isImageExtension = f.name && /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(f.name);
                            return isImageType || isImageExtension;
                        }).length > 0 && (
                                <div>
                                    <h3 className="text-lg font-medium mb-4">{t("plumberForm.uploadedPhotos")}</h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                        {uploadedFiles
                                            .filter(f => {
                                                const isImageType = f.type && f.type.startsWith("image/");
                                                const isImageExtension = f.name && /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(f.name);
                                                return isImageType || isImageExtension;
                                            })
                                            .map((file) => (
                                                <div key={file.id} className="relative aspect-square">
                                                    <img
                                                        src={file.url}
                                                        alt={file.name}
                                                        className="w-full h-full object-cover rounded-lg"
                                                    />
                                                    <button
                                                        onClick={() => handleRemoveUploadedFile(file.id)}
                                                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600">
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg">
                                                        {file.name}
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}

                        {/* Display Uploaded Videos */}
                        {uploadedFiles.filter(f => {
                            const isVideoType = f.type && f.type.startsWith("video/");
                            const isVideoExtension = f.name && /\.(mp4|webm|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)$/i.test(f.name);
                            return isVideoType || isVideoExtension;
                        }).length > 0 && (
                                <div>
                                    <h3 className="text-lg font-medium mb-4">{t("plumberForm.uploadedVideos")}</h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                        {uploadedFiles
                                            .filter(f => {
                                                const isVideoType = f.type && f.type.startsWith("video/");
                                                const isVideoExtension = f.name && /\.(mp4|webm|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)$/i.test(f.name);
                                                return isVideoType || isVideoExtension;
                                            })
                                            .map((file) => (
                                                <div key={file.id} className="relative aspect-square">
                                                    <video
                                                        src={file.url}
                                                        className="w-full h-full object-cover rounded-lg"
                                                        controls
                                                        muted
                                                    />
                                                    <button
                                                        onClick={() => handleRemoveUploadedFile(file.id)}
                                                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600">
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg">
                                                        {file.name}
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}

                        {/* Display Captured Photos */}
                        {capturedPhotos.length > 0 && (
                            <div>
                                <h3 className="text-lg font-medium mb-4">{t("plumberForm.capturedPhotos")}</h3>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                    {capturedPhotos.map((photo, index) => (
                                        <div key={index} className="relative aspect-square">
                                            <img
                                                src={photo}
                                                alt={`Captured photo ${index + 1}`}
                                                className="w-full h-full object-cover rounded-lg"
                                            />
                                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg">
                                                {t("plumberForm.capturedPhotoNumber", { number: index + 1 })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Display Recorded Video */}
                        {recordedVideo && (
                            <div>
                                <h3 className="text-lg font-medium mb-4">{t("plumberForm.recordedVideo")}</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="relative aspect-video">
                                        <video
                                            src={URL.createObjectURL(recordedVideo)}
                                            className="w-full h-full object-cover rounded-lg"
                                            controls
                                            muted
                                        />
                                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg">
                                            {t("plumberForm.recordedVideo")}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Live Capture */}
                        <div>
                            {/* Desktop only checkbox to show/hide camera section */}
                            <div className="hidden sm:flex items-center gap-2 mb-4">
                                <input
                                    type="checkbox"
                                    id="showCameraSection"
                                    checked={showCameraSection}
                                    onChange={(e) => {
                                        setShowCameraSection(e.target.checked);
                                        // Clean up camera if hiding the section
                                        if (!e.target.checked) {
                                            cleanupCamera();
                                        }
                                    }}
                                    className="h-4 w-4 text-blue-600 rounded"
                                />
                                <label htmlFor="showCameraSection" className="text-lg font-medium cursor-pointer">
                                    {t("plumberForm.takePhotoVideo")}
                                </label>
                            </div>

                            {/* Mobile always shows the title */}
                            <h3 className="text-lg font-medium mb-4 sm:hidden">{t("plumberForm.takePhotoVideo")}</h3>

                            {/* Show camera section on mobile or when checkbox is checked on desktop */}
                            {(showCameraSection || isMobile) && (
                                <div className="space-y-4">
                                    <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: "12/9" }}>
                                        <video
                                            ref={videoRef}
                                            autoPlay
                                            playsInline
                                            muted
                                            className={`w-full h-full object-cover ${isCapturing ? "block" : "hidden"}`}
                                        />
                                        {!isCapturing && (
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <Button onClick={startCamera}>
                                                    <Camera className="h-5 w-5 mr-2" />
                                                    {t("plumberForm.startCameraButton")}
                                                </Button>
                                            </div>
                                        )}
                                        {isCapturing && (
                                            <>
                                                {/* Recording Timer */}
                                                {isRecording && (
                                                    <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                                                        {t("plumberForm.recordingLabel")} {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, "0")}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {/* Camera Controls - Moved outside the video area */}
                                    {isCapturing && (
                                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
                                            <Button
                                                onClick={capturePhoto}
                                                disabled={isUploading || isRecording || getCurrentImageCount() >= 5}
                                                className="bg-blue-600 hover:bg-blue-700 text-sm px-4 py-2 flex-1 sm:flex-none sm:w-auto"
                                            >
                                                <Camera className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                                                <span>
                                                    {isUploading ? t("plumberForm.uploadingLabel") : t("plumberForm.takePhotoButton", { current: getCurrentImageCount() })}
                                                </span>
                                            </Button>

                                            {!isRecording ? (
                                                <Button
                                                    onClick={startVideoRecording}
                                                    disabled={isUploading || getCurrentVideoCount() >= 1}
                                                    className="bg-red-600 hover:bg-red-700 text-sm px-4 py-2 flex-1 sm:flex-none sm:w-auto"
                                                >
                                                    <Video className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                                                    <span>
                                                        {recordedVideo ? t("plumberForm.videoRecorded") : t("plumberForm.recordVideoButton")}
                                                    </span>
                                                </Button>
                                            ) : (
                                                <Button
                                                    onClick={stopVideoRecording}
                                                    className="bg-red-600 hover:bg-red-700 text-sm px-4 py-2 flex-1 sm:flex-none sm:w-auto"
                                                >
                                                    <Video className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                                                    <span>{t("plumberForm.stopRecordingButton")}</span>
                                                </Button>
                                            )}

                                            <Button
                                                variant="outline"
                                                onClick={cleanupCamera}
                                                className="text-sm px-4 py-2 flex-1 sm:flex-none sm:w-auto"
                                            >
                                                <span>{t("plumberForm.stopCameraButton")}</span>
                                            </Button>
                                        </div>
                                    )}

                                    {/* Hidden canvas for photo capture */}
                                    <canvas ref={canvasRef} className="hidden" />

                                    {/* Media Limits Info */}
                                    <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                                        <p><strong>{t("plumberForm.mediaLimitsTitle")}</strong></p>
                                        <p>{t("plumberForm.maxImagesNote")}</p>
                                        <p>{t("plumberForm.maxVideosNote")}</p>
                                        <p>{t("plumberForm.currentMediaCount", { imageCount: getCurrentImageCount(), videoCount: getCurrentVideoCount() })}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Card>
                <br></br>

                {/* Contact Information */}
                <Card className="p-6" style={{ textAlign: "left" }}>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold">{t("plumberForm.contactInformationLabel")}</h2>
                        {user && !isEditMode && (user.firstName || user.email || user.phone) && (
                            <span className="text-sm text-green-600 bg-green-50 px-2 py-1 rounded">
                                {t("plumberForm.autoFilledFromProfile")}
                            </span>
                        )}
                    </div>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">{t("plumberForm.fullNameLabel")}</label>
                            <input
                                type="text"
                                value={formData.contactName}
                                onChange={e => handleFieldChange("contactName", e.target.value)}
                                onBlur={() => handleFieldBlur("contactName")}
                                className={`w-full px-4 py-2 border rounded-lg ${errors.contactName && (touched.contactName || isSubmitted) ? "border-red-500" : "border-gray-300"} ${user && user.firstName && user.lastName && !isEditMode ? "bg-green-50" : ""}`}
                                placeholder={t("plumberForm.fullNamePlaceholder")}
                            />
                            {errors.contactName && (touched.contactName || isSubmitted) && (
                                <p className="text-sm text-red-600 mt-1">{errors.contactName}</p>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("plumberForm.emailLabel")}</label>
                                <input
                                    type="email"
                                    value={formData.contactEmail}
                                    onChange={e => handleFieldChange("contactEmail", e.target.value)}
                                    onBlur={() => handleFieldBlur("contactEmail")}
                                    className={`w-full px-4 py-2 border rounded-lg ${errors.contactEmail && (touched.contactEmail || isSubmitted) ? "border-red-500" : "border-gray-300"} ${user && user.email && !isEditMode ? "bg-green-50" : ""}`}
                                    placeholder={t("plumberForm.emailPlaceholder")}
                                />
                                {errors.contactEmail && (touched.contactEmail || isSubmitted) && (
                                    <p className="text-sm text-red-600 mt-1">{errors.contactEmail}</p>
                                )}
                                <p className="mt-1 text-sm text-gray-500">
                                    {t("plumberForm.onlyVisibleAfterPurchase")}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">{t("plumberForm.phoneNumberLabel")}</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-black-500 text-sm">+</span>
                                    </div>
                                    <input
                                        type="tel"
                                        value={formData.contactPhone.replace(/^\+/, "")}
                                        onChange={(e) => {
                                            let value = e.target.value.replace(/\D/g, "");
                                            if (value) {
                                                value = "+" + value;
                                            }
                                            handleFieldChange("contactPhone", value);
                                        }}
                                        placeholder={t("plumberForm.phoneNumberPlaceholder")}
                                        className={`w-full pl-8 pr-3 py-2 border rounded-lg ${errors.contactPhone && (touched.contactPhone || isSubmitted) ? "border-red-500" : "border-gray-300"} ${user && user.phone && !isEditMode ? "bg-green-50" : ""}`}
                                    />
                                </div>
                                {errors.contactPhone && (touched.contactPhone || isSubmitted) && (
                                    <p className="text-sm text-red-600 mt-1">{errors.contactPhone}</p>
                                )}
                                <p className="mt-1 text-sm text-gray-500">
                                    {t("plumberForm.onlyVisibleAfterPurchase")}
                                </p>
                            </div>
                        </div>

                        {/* WhatsApp Section */}

                        <div className="border-t pt-4">
                            <div className="flex items-center justify-between mb-4">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={formData.canContactByWhatsapp}
                                        onChange={e => {
                                            setFormData(prev => ({
                                                ...prev,
                                                canContactByWhatsapp: e.target.checked,
                                                contactWhatsapp: e.target.checked ? prev.contactWhatsapp : "",
                                            }));
                                        }}
                                        className="h-4 w-4 text-blue-600 rounded"
                                    />
                                    <span>{t("plumberForm.wantWhatsappContact")}</span>
                                </label>
                                {formData.canContactByWhatsapp && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            // Auto-fill with phone number if available
                                            setFormData(prev => ({
                                                ...prev,
                                                contactWhatsapp: prev.contactPhone,
                                            }));
                                        }}
                                        className="text-sm text-blue-600 hover:text-blue-700">
                                        {t("plumberForm.usePhoneNumberButton")}
                                    </button>
                                )}
                            </div>

                            {formData.canContactByWhatsapp && (
                                <div className="space-y-2">
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <span className="text-black-500 text-sm">+</span>
                                        </div>
                                        <input
                                            type="tel"
                                            value={formData.contactWhatsapp.replace(/^\+/, "")}
                                            onChange={(e) => {
                                                let value = e.target.value.replace(/\D/g, "");
                                                if (value) {
                                                    value = "+" + value;
                                                }
                                                handleFieldChange("contactWhatsapp", value);
                                            }}
                                            placeholder={t("plumberForm.whatsappNumberPlaceholder")}
                                            className={`w-full pl-8 pr-3 py-2 border rounded-lg ${errors.contactWhatsapp && (touched.contactWhatsapp || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                        />
                                    </div>
                                    {errors.contactWhatsapp && (touched.contactWhatsapp || isSubmitted) && (
                                        <p className="text-sm text-red-600 mt-1">{errors.contactWhatsapp}</p>
                                    )}
                                    <p className="text-sm text-gray-500">
                                        {t("plumberForm.whatsappHelper")}
                                    </p>
                                </div>
                            )}
                        </div>

                    </div>
                </Card>
                <br></br>

                {/* Terms and Conditions - Only show for create mode */}
                {!isEditMode && (
                    <Card className="p-6">
                        <h2 className="text-xl font-semibold mb-4">{t("plumberForm.termsAndConditionsLabel")}</h2>
                        <div className="space-y-4">
                            <div className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    id="acceptTerms"
                                    checked={acceptTerms}
                                    onChange={(e) => {
                                        setAcceptTerms(e.target.checked);
                                        // Clear error when user checks/unchecks
                                        if (errors.acceptTerms) {
                                            setErrors(prev => ({
                                                ...prev,
                                                acceptTerms: ""
                                            }));
                                        }
                                    }}
                                    className={`h-4 w-4 text-blue-600 rounded mt-0.5 ${errors.acceptTerms && (touched.acceptTerms || isSubmitted) ? "border-red-500" : ""}`}
                                />
                                <label htmlFor="acceptTerms" className="text-sm text-gray-700 cursor-pointer">
                                    {t("plumberForm.acceptTermsPrefix")}{" "}
                                    <a
                                        href="/terms-and-conditions"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 underline"
                                    >
                                        {t("plumberForm.termsAndConditionsLink")}
                                    </a>
                                    {" "}{t("plumberForm.andLabel")}{" "}
                                    <a
                                        href="/privacy-policy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 underline"
                                    >
                                        {t("plumberForm.privacyPolicyLink")}
                                    </a>
                                    . {t("plumberForm.acceptTermsSuffix")}
                                </label>
                            </div>
                            {errors.acceptTerms && (touched.acceptTerms || isSubmitted) && (
                                <p className="text-sm text-red-600">{errors.acceptTerms}</p>
                            )}
                        </div>
                    </Card>
                )}
                <br></br>

                {/* Submit Button */}
                <div className="flex justify-between mt-8">
                    <Button
                        variant="outline"
                        onClick={onBack}
                        className="px-6">
                        {t("plumberForm.backButton")}
                    </Button>
                    <Button
                        onClick={() => {
                            // Prevent submission while uploading files
                            if (isUploading) {
                                GlobalService.showToast("warning", t("common.warning"), "Please wait for file upload to complete before submitting.");
                                return;
                            }

                            setIsSubmitted(true);
                            // Mark terms as touched if in create mode
                            if (!isEditMode) {
                                setTouched(prev => ({
                                    ...prev,
                                    acceptTerms: true
                                }));
                            }
                            if (validateForm()) {
                                const submissionData = prepareFormDataForSubmission();
                                console.log("Submitting form data:", submissionData);
                                if (onSubmit) {
                                    onSubmit(submissionData);
                                }
                            } else {
                                GlobalService.showToast("error", t("plumberForm.validationErrorTitle"), t("plumberForm.fixFormErrors"));
                            }
                        }}
                        disabled={isUploading}
                        className="px-8 py-3">
                        {isUploading ? t("plumberForm.uploadingFiles") || "Uploading files..." : (isEditMode ? t("plumberForm.updateRequestButton") : t("plumberForm.submitRequestButton"))}
                    </Button>
                </div>

            </div>
        </div>
    );
};

export default PlumberRequestForm;
